// BoysHostelManagement.js - Boys Hostel Management
// Concept is IDENTICAL to GirlsHostelManagement.js — only the hostel name,
// gender and demo data differ. Keep both files in sync when editing logic.
//
// Structure: Hostel -> Blocks (e.g. Junior Block / Senior Block, name is
// editable) -> Floors (each floor maps to one or more Classes, editable) ->
// Rooms (editable capacity/notes) + Washrooms (editable shower/light/
// washbasin counts per washroom).
//
// Tabs: Requests | Rooms & Washrooms | Register Student |
//       Complaints | Fee | Mess Menu | Staff | Inventory | Messages
// Register Student, Rooms & Washrooms, Mess Menu, Messages, Inventory and
// Requests are backed by the API (see /api/hostel/<HostelType>/...) and
// persisted in MongoDB. Requests are raised by parents from the Parent
// Portal (Leave Request button on ParentsDashboard.js) — there is no
// manual "New Request" entry here anymore; the Warden only Approves /
// Rejects / marks a request Returned. Complaints, Fee and Staff remain
// pure UI with local React state only (no backend calls), as before.
import { useState, useMemo, useEffect, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { getSchoolName } from "./schoolNameStore";
import { User, DoorOpen, Footprints, Home, Check, X, Building2, Pencil, Trash2, Save, Plus, AlertTriangle, ShowerHead, Lightbulb, Droplet, Pin, Printer, BedDouble, ClipboardList, HardHat, Calendar, Phone, GraduationCap, Coins, Search, BarChart3, ArrowLeftRight, Circle, PenLine, School, Zap, Armchair, Brush, Wifi, Wrench, Wind, Feather, Package } from "lucide-react";
const HOSTEL_NAME = "Boys Hostel";
const GENDER = "Male";
const API_BASE = "https://schoolcrm-fzcv.onrender.com/api/hostel";
const STAFF_API = "https://schoolcrm-fzcv.onrender.com/api/staff";
const HOSTEL_TYPE = "Boys";
const WARDEN_LABEL = `${HOSTEL_TYPE} Warden`; // used only as a display label in chats

const NAVY = "#0D47A1";
const BLUE = "#2196F3";
const LIGHT = "#90CAF9";
const PALE = "#E3F2FD";
const STATUS_COLOR = {
  Pending: {
    bg: "#FFF3E0",
    text: "#E65100",
    dot: "#FB8C00"
  },
  Approved: {
    bg: "#E8F5E9",
    text: "#2E7D32",
    dot: "#43A047"
  },
  Rejected: {
    bg: "#FFEBEE",
    text: "#C62828",
    dot: "#E53935"
  }
};
const TYPE_COLOR = {
  Leave: {
    bg: NAVY,
    label: "Leave"
  },
  Permission: {
    bg: BLUE,
    label: "Permission"
  }
};
const COMPLAINT_STATUS_COLOR = {
  Pending: {
    bg: "#FFF3E0",
    text: "#E65100",
    dot: "#FB8C00"
  },
  "In Progress": {
    bg: "#E3F2FD",
    text: "#1565C0",
    dot: "#2196F3"
  },
  Resolved: {
    bg: "#E8F5E9",
    text: "#2E7D32",
    dot: "#43A047"
  }
};
const CATEGORY_ICON = {
  Electrical: <Lightbulb className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  Plumbing: <Droplet className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  Furniture: <Armchair className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  Cleanliness: <Brush className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  Internet: <Wifi className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  Other: <Wrench className="inline w-[1em] h-[1em] align-[-0.125em]" />
};

// Maps each complaint category to the Worker "role" keywords (from
// /api/workers, e.g. Electrician / Plumber / Carpenter / Gardener / Peon /
// Cook / Driver) that should show up in the "Assign" dropdown for that
// category. Matching is case-insensitive substring matching against the
// worker's role. If no worker's role matches, all active workers are shown
// as a fallback so the complaint can still be assigned.
const CATEGORY_ROLE_MAP = {
  Electrical: ["electric"],
  Plumbing: ["plumb"],
  Furniture: ["carpent", "furniture"],
  Cleanliness: ["clean", "sweep", "housekeep", "peon", "garden"],
  Internet: ["technician", "network", "it support", "electric"],
  Other: []
};
const SCHOOL_NAME = () => `${getSchoolName()} Group of Schools`;
const FEE_STATUS_COLOR = {
  Paid: {
    bg: "#E8F5E9",
    text: "#2E7D32",
    dot: "#43A047"
  },
  Pending: {
    bg: "#FFF3E0",
    text: "#E65100",
    dot: "#FB8C00"
  },
  Overdue: {
    bg: "#FFEBEE",
    text: "#C62828",
    dot: "#E53935"
  }
};
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MEALS = [{
  key: "breakfast",
  label: "Breakfast"
}, {
  key: "lunch",
  label: " Lunch"
}, {
  key: "dinner",
  label: "Dinner"
}];

// Furniture items tracked per room (Inventory tab)
const INVENTORY_ITEMS = [{
  key: "fan",
  label: "Fan",
  icon: <Wind className="inline w-[1em] h-[1em] align-[-0.125em]" />
}, {
  key: "kattil",
  label: "Cot",
  icon: ""
}, {
  key: "pillow",
  label: "Pillow",
  icon: <Feather className="inline w-[1em] h-[1em] align-[-0.125em]" />
}, {
  key: "bench",
  label: "Bench",
  icon: ""
}, {
  key: "chair",
  label: "Chair",
  icon: <Armchair className="inline w-[1em] h-[1em] align-[-0.125em]" />
}, {
  key: "cupboard",
  label: "Cupboard",
  icon: <Package className="inline w-[1em] h-[1em] align-[-0.125em]" />
}, {
  key: "light",
  label: "Light",
  icon: <Lightbulb className="inline w-[1em] h-[1em] align-[-0.125em]" />
}];
function blankInventory() {
  return Object.fromEntries(INVENTORY_ITEMS.map(i => [i.key, 0]));
}
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch {
    return d;
  }
}
function fmtMoney(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN");
}
function newId(prefix) {
  return prefix + "_" + Date.now() + "_" + Math.floor(Math.random() * 1000);
}

// ── Responsive helper (mobile vs desktop) ────────────────────────────────
// Pure layout helper — only used to switch fixed multi-column grids to a
// single stacked column on narrow screens. Nothing else about the page
// changes; desktop layout (≥768px) is untouched.
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= breakpoint : false);
  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth <= breakpoint);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

// ── Academic Year (Batch) helpers ────────────────────────────────────────
// School year runs June -> May, so Jan-May still belongs to the year that
// started the previous June.
function currentAcademicYear() {
  const now = new Date();
  const y = now.getFullYear();
  const startYear = now.getMonth() >= 5 ? y : y - 1; // getMonth() 5 = June
  return `${startYear}-${startYear + 1}`;
}
function academicYearOptions() {
  const now = new Date();
  const y = now.getFullYear();
  const startYear = now.getMonth() >= 5 ? y : y - 1;
  const out = [];
  for (let i = -1; i <= 3; i++) {
    out.push(`${startYear + i}-${startYear + i + 1}`);
  }
  return out;
}

// ── Room / Washroom generators ───────────────────────────────────────────
function genRooms(prefix, floorNo, count, capacity = 4) {
  return Array.from({
    length: count
  }, (_, i) => ({
    id: newId(`room_${prefix}${floorNo}`),
    roomNo: `${prefix}${floorNo}${String(i + 1).padStart(2, "0")}`,
    capacity,
    notes: ""
  }));
}
function genWashrooms(prefix, floorNo, count = 5) {
  return Array.from({
    length: count
  }, (_, i) => ({
    id: newId(`wr_${prefix}${floorNo}`),
    label: `Washroom ${i + 1}`,
    showers: 2,
    lights: 3,
    washbasins: 2
  }));
}
function allRooms(blocks) {
  const out = [];
  blocks.forEach(b => b.floors.forEach(f => f.rooms.forEach(r => out.push({
    ...r,
    blockId: b.id,
    blockName: b.name,
    floorId: f.id,
    floorNo: f.floorNo,
    classes: f.classes
  }))));
  return out;
}
function findRoomMeta(blocks, roomId) {
  return allRooms(blocks).find(r => r.id === roomId) || null;
}
function findMatchingFloor(blocks, className) {
  const cls = (className || "").trim();
  if (!cls) return null;
  for (const b of blocks) {
    for (const f of b.floors) {
      if (f.classes.map(c => c.trim()).includes(cls)) return {
        blockId: b.id,
        floorId: f.id
      };
    }
  }
  return null;
}
const FEE_TERMS = ["Term 1 (Jun - Sep)", "Term 2 (Oct - Jan)", "Term 3 (Feb - May)"];
const emptyRegForm = {
  name: "",
  address: "",
  parentNumber: "",
  altNumber: "",
  schoolName: SCHOOL_NAME(),
  className: "",
  section: "",
  blockId: "",
  floorId: "",
  roomId: "",
  academicYear: currentAcademicYear()
};
const emptyRoomForm = {
  roomNo: "",
  capacity: "4",
  notes: ""
};
const emptyWashroomForm = {
  label: "",
  showers: "2",
  lights: "3",
  washbasins: "2"
};
const emptyComplaintForm = {
  studentId: "",
  category: "Electrical",
  description: ""
};
const emptyMessageForm = {
  priority: "Normal",
  body: ""
};
export default function BoysHostelManagement() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("requests");
  const [statusFilter, setStatusFilter] = useState("All");
  const [blocks, setBlocks] = useState([]);
  const [activeBlockId, setActiveBlockId] = useState("");
  const [activeFloorId, setActiveFloorId] = useState("");
  const [editingBlockName, setEditingBlockName] = useState(false);
  const [blockNameDraft, setBlockNameDraft] = useState("");
  const [editingFloorClasses, setEditingFloorClasses] = useState(false);
  const [floorClassesDraft, setFloorClassesDraft] = useState("");
  const [editingRoomId, setEditingRoomId] = useState("");
  const [roomDraft, setRoomDraft] = useState({
    roomNo: "",
    capacity: "",
    notes: ""
  });
  const [editingWashroomId, setEditingWashroomId] = useState("");
  const [washroomDraft, setWashroomDraft] = useState({
    label: "",
    showers: "",
    lights: "",
    washbasins: ""
  });
  const [students, setStudents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [rules, setRules] = useState([]);
  const [newRule, setNewRule] = useState("");

  // ── API load / save state ──────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [structureError, setStructureError] = useState("");
  const [messMenuError, setMessMenuError] = useState("");
  const [inventoryError, setInventoryError] = useState("");
  const structureSaveTimer = useRef(null);
  const messMenuSaveTimer = useRef(null);
  const [regForm, setRegForm] = useState(emptyRegForm);
  const [regError, setRegError] = useState("");
  const [regSuccess, setRegSuccess] = useState("");
  const [regAutoMatched, setRegAutoMatched] = useState(false);
  const [regYearFilter, setRegYearFilter] = useState("All");
  const [admissionsList, setAdmissionsList] = useState([]);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const nameFieldRef = useRef(null);
  const [editingStudentId, setEditingStudentId] = useState("");
  const [studentEditForm, setStudentEditForm] = useState(null);
  const [studentEditError, setStudentEditError] = useState("");
  const [roomForm, setRoomForm] = useState(emptyRoomForm);
  const [roomError, setRoomError] = useState("");
  const [washroomForm, setWashroomForm] = useState(emptyWashroomForm);
  const [washroomError, setWashroomError] = useState("");
  const [newFloorClasses, setNewFloorClasses] = useState("");
  const [newBlockName, setNewBlockName] = useState("");
  const [complaints, setComplaints] = useState([]);
  const [complaintForm, setComplaintForm] = useState(emptyComplaintForm);
  const [complaintError, setComplaintError] = useState("");
  const [complaintFilter, setComplaintFilter] = useState("All");
  const [assignInputs, setAssignInputs] = useState({});
  const [workers, setWorkers] = useState([]); // fetched from /api/workers, used to populate the Assign dropdown

  // Read-only Hostel Fee view — fetched from /api/hostel/:hostelType/fee-summary
  // (Warden cannot add/edit fees here; that happens in the admin Fee Ledger).
  const [feeSummary, setFeeSummary] = useState([]);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeLoadError, setFeeLoadError] = useState("");
  const [messMenu, setMessMenu] = useState({});
  const [messDay, setMessDay] = useState(DAYS[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1]);

  // Staff tab is READ-ONLY here — it only shows this hostel's own Warden
  // (and Sub Warden, if one exists) exactly as created in Staff Management
  // (dept: "Hostel"). No other staff, and no add/remove from this page.
  const [staff, setStaff] = useState([]);
  const [staffLoadError, setStaffLoadError] = useState("");
  const [roomInventory, setRoomInventory] = useState({}); // roomId -> counts
  const [inventorySearch, setInventorySearch] = useState("");
  const [editingInventoryRoomId, setEditingInventoryRoomId] = useState("");
  const [inventoryDraft, setInventoryDraft] = useState(null);
  const [inventorySavingRoomId, setInventorySavingRoomId] = useState("");
  const [messages, setMessages] = useState([]);
  const [messageForm, setMessageForm] = useState(emptyMessageForm);
  const [messageError, setMessageError] = useState("");

  // ── API helpers: load & save (Register Student, Rooms & Washrooms,
  // Mess Menu, Messages, Inventory persist to MongoDB via /api/hostel/...) ──
  function normStructure(doc) {
    const blocksOut = (doc?.blocks || []).map(b => ({
      id: b._id || b.id,
      name: b.name,
      floors: (b.floors || []).map(f => ({
        id: f._id || f.id,
        floorNo: f.floorNo,
        classes: f.classes || [],
        rooms: (f.rooms || []).map(r => ({
          id: r._id || r.id,
          roomNo: r.roomNo,
          capacity: r.capacity,
          notes: r.notes || ""
        })),
        washrooms: (f.washrooms || []).map(w => ({
          id: w._id || w.id,
          label: w.label,
          showers: w.showers,
          lights: w.lights,
          washbasins: w.washbasins
        }))
      }))
    }));
    return {
      blocks: blocksOut,
      rules: doc?.rules || []
    };
  }
  async function saveStructure(nextBlocks, nextRules) {
    setStructureError("");
    try {
      const res = await fetch(`${API_BASE}/${HOSTEL_TYPE}/structure`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          blocks: nextBlocks,
          rules: nextRules
        })
      });
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      const norm = normStructure(data);
      setBlocks(norm.blocks);
      setRules(norm.rules);
      return norm;
    } catch (err) {
      console.error("saveStructure error:", err);
      setStructureError(" Could not save changes to the server. Please check your connection and try again.");
      return null;
    }
  }

  // Debounced save for high-frequency edits (typing in room/washroom fields):
  // updates local state immediately, persists shortly after typing stops.
  function scheduleStructureSave(nextBlocks, nextRules) {
    setBlocks(nextBlocks);
    if (structureSaveTimer.current) clearTimeout(structureSaveTimer.current);
    structureSaveTimer.current = setTimeout(() => {
      saveStructure(nextBlocks, nextRules);
    }, 600);
  }
  async function saveMessMenuToServer(nextMenu) {
    setMessMenuError("");
    try {
      const res = await fetch(`${API_BASE}/${HOSTEL_TYPE}/messmenu`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          menu: nextMenu
        })
      });
      if (!res.ok) throw new Error("Save failed");
    } catch (err) {
      console.error("saveMessMenuToServer error:", err);
      setMessMenuError(" Could not save the menu changes to the server.");
    }
  }
  async function saveInventoryToServer(roomId, counts) {
    setInventoryError("");
    try {
      const res = await fetch(`${API_BASE}/${HOSTEL_TYPE}/inventory/${roomId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(counts)
      });
      if (!res.ok) throw new Error("Save failed");
      return true;
    } catch (err) {
      console.error("saveInventoryToServer error:", err);
      setInventoryError(" Could not save inventory changes to the server.");
      return false;
    }
  }
  async function loadAllHostelData() {
    setLoading(true);
    setLoadError("");
    try {
      const [structRes, studentsRes, menuRes, messagesRes, inventoryRes, complaintsRes, requestsRes] = await Promise.all([fetch(`${API_BASE}/${HOSTEL_TYPE}/structure`), fetch(`${API_BASE}/${HOSTEL_TYPE}/students`), fetch(`${API_BASE}/${HOSTEL_TYPE}/messmenu`), fetch(`${API_BASE}/${HOSTEL_TYPE}/messages`), fetch(`${API_BASE}/${HOSTEL_TYPE}/inventory`), fetch(`${API_BASE}/${HOSTEL_TYPE}/complaints`), fetch(`${API_BASE}/${HOSTEL_TYPE}/requests`)]);
      if (!structRes.ok || !studentsRes.ok || !menuRes.ok || !messagesRes.ok || !inventoryRes.ok || !complaintsRes.ok || !requestsRes.ok) {
        throw new Error("One or more requests failed");
      }
      const [structData, studentsData, menuData, messagesData, inventoryData, complaintsData, requestsData] = await Promise.all([structRes.json(), studentsRes.json(), menuRes.json(), messagesRes.json(), inventoryRes.json(), complaintsRes.json(), requestsRes.json()]);
      const norm = normStructure(structData);
      setBlocks(norm.blocks);
      setRules(norm.rules);
      setActiveBlockId(norm.blocks[0]?.id || "");
      setActiveFloorId(norm.blocks[0]?.floors[0]?.id || "");
      setStudents((studentsData || []).map(s => ({
        id: s._id,
        name: s.name,
        address: s.address,
        parentNumber: s.parentNumber,
        altNumber: s.altNumber,
        schoolName: s.schoolName,
        className: s.className,
        section: s.section,
        gender: s.gender,
        roomId: s.roomId,
        academicYear: s.academicYear || "",
        createdAt: s.createdAt || ""
      })));
      setMessMenu(menuData?.menu || {});
      setMessages((messagesData || []).map(m => ({
        id: m._id,
        from: m.from,
        priority: m.priority,
        body: m.body,
        createdAt: m.createdAt
      })));
      const invMap = {};
      (inventoryData || []).forEach(it => {
        invMap[it.roomId] = {
          fan: it.fan,
          kattil: it.kattil,
          pillow: it.pillow,
          bench: it.bench,
          chair: it.chair,
          cupboard: it.cupboard,
          light: it.light
        };
      });
      setRoomInventory(invMap);
      setComplaints((complaintsData || []).map(c => ({
        id: c._id,
        studentId: c.studentId,
        category: c.category,
        description: c.description,
        status: c.status,
        assignedTo: c.assignedTo || "",
        assignedWorkerId: c.assignedWorkerId || "",
        createdAt: c.createdAt
      })));
      setRequests((requestsData || []).map(normalizeRequest));
    } catch (err) {
      console.error("loadAllHostelData error:", err);
      setLoadError(" Could not load hostel data from the server. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  // Loads this hostel's Warden (and Sub Warden, if any) from the real Staff
  // collection. Best-effort / separate from loadAllHostelData so a hiccup
  // here never blocks the rest of the page from loading.
  async function loadHostelStaff() {
    setStaffLoadError("");
    try {
      const res = await fetch(STAFF_API);
      if (!res.ok) throw new Error("Failed to load staff");
      const data = await res.json();
      setStaff(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("loadHostelStaff error:", err);
      setStaffLoadError(" Could not load staff from the server.");
    }
  }
  useEffect(() => {
    loadAllHostelData();
    loadHostelStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the read-only Hostel Fee table whenever the Fee tab is opened.
  useEffect(() => {
    if (activeTab === "fee") loadFeeSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load workers (support staff: Electrician, Plumber, Carpenter, Gardener,
  // Peon, Cook, Driver, etc.) so the Complaints tab can offer a dropdown of
  // real staff to assign, matched to the complaint category.
  useEffect(() => {
    fetch("https://schoolcrm-fzcv.onrender.com/api/workers").then(res => res.json()).then(data => setWorkers(Array.isArray(data) ? data : [])).catch(err => console.error("Failed to load workers:", err));
  }, []);

  // Close the STUDENT NAME suggestions dropdown when clicking outside it.
  useEffect(() => {
    function handleOutsideClick(e) {
      if (nameFieldRef.current && !nameFieldRef.current.contains(e.target)) {
        setShowNameSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Load all admissions once so the Register Student "STUDENT NAME" field
  // can auto-suggest matching students and auto-fill their details.
  // Best-effort — never blocks the rest of the page.
  useEffect(() => {
    fetch("https://schoolcrm-fzcv.onrender.com/api/admissions").then(res => res.json()).then(data => setAdmissionsList(Array.isArray(data) ? data : [])).catch(err => console.error("Failed to load admissions for autofill:", err));
  }, []);

  // Turns an Admission's "Class 9" / "9" applyingClass into the plain digit
  // (or LKG/UKG) format the hostel's own CLASS field uses.
  function classDigitsFromAdmission(applyingClass) {
    const val = (applyingClass || "").trim();
    if (/^(LKG|UKG)$/i.test(val)) return val.toUpperCase();
    const digits = val.replace(/[^0-9]/g, "");
    return digits || val;
  }
  function handleRegNameChange(value) {
    setRegForm(f => ({
      ...f,
      name: value
    }));
    const q = value.trim().toLowerCase();
    if (q.length < 2) {
      setNameSuggestions([]);
      setShowNameSuggestions(false);
      return;
    }
    const matches = admissionsList.filter(a => (a.studentName || "").toLowerCase().includes(q)).slice(0, 8);
    setNameSuggestions(matches);
    setShowNameSuggestions(matches.length > 0);
  }

  // Picking a suggestion auto-fills Address, Parent/Alt Number, Class &
  // Section (and re-runs the Block/Floor auto-match) from that student's
  // Admission record. If several admitted students share the same name,
  // the dropdown lists Class/Section/Father's name for each so the right
  // one can be picked.
  function selectNameSuggestion(adm) {
    setRegForm(f => ({
      ...f,
      name: adm.studentName || f.name,
      address: [adm.address, adm.city].filter(Boolean).join(", ") || f.address,
      parentNumber: adm.fatherMobile || f.parentNumber,
      altNumber: adm.motherMobile || f.altNumber,
      section: adm.section || f.section
    }));
    handleClassChange(classDigitsFromAdmission(adm.applyingClass));
    setShowNameSuggestions(false);
    setNameSuggestions([]);
  }

  // ── Derived data ──────────────────────────────────────────────────────
  const flatRooms = useMemo(() => allRooms(blocks), [blocks]);
  const activeBlock = useMemo(() => blocks.find(b => b.id === activeBlockId) || blocks[0], [blocks, activeBlockId]);
  const activeFloor = useMemo(() => activeBlock?.floors.find(f => f.id === activeFloorId) || activeBlock?.floors[0], [activeBlock, activeFloorId]);
  const occupancyByRoom = useMemo(() => {
    const map = {};
    students.forEach(s => {
      if (s.roomId) map[s.roomId] = (map[s.roomId] || 0) + 1;
    });
    return map;
  }, [students]);
  const roomStats = useMemo(() => {
    const totalRooms = flatRooms.length;
    const totalCapacity = flatRooms.reduce((sum, r) => sum + Number(r.capacity || 0), 0);
    const totalOccupied = students.filter(s => s.roomId).length;
    return {
      totalRooms,
      totalCapacity,
      totalOccupied,
      available: totalCapacity - totalOccupied
    };
  }, [flatRooms, students]);
  const inventoryTotals = useMemo(() => {
    const totals = blankInventory();
    flatRooms.forEach(r => {
      const inv = roomInventory[r.id] || {};
      INVENTORY_ITEMS.forEach(item => {
        totals[item.key] += Number(inv[item.key] || 0);
      });
    });
    return totals;
  }, [flatRooms, roomInventory]);
  const inventoryRoomsFiltered = useMemo(() => {
    const q = inventorySearch.trim().toLowerCase();
    if (!q) return flatRooms;
    return flatRooms.filter(r => String(r.roomNo).toLowerCase().includes(q) || String(r.blockName || "").toLowerCase().includes(q));
  }, [flatRooms, inventorySearch]);
  const studentById = id => students.find(s => s.id === id);
  const roomPathFor = roomId => {
    const m = findRoomMeta(blocks, roomId);
    if (!m) return "—";
    return `${m.blockName} · Floor ${m.floorNo} · Room ${m.roomNo}`;
  };

  // This hostel's own staff — filtered from the real Staff collection by
  // role prefix (e.g. "Boys Hostel Warden", "Boys Hostel Sub Warden"), the
  // way AddWardenModal in Staff Management names them. Warden shown first,
  // then Sub Warden, then anything else tagged for this hostel.
  const hostelStaff = useMemo(() => {
    const rolePrefix = `${HOSTEL_TYPE} Hostel`;
    const rank = role => {
      const r = (role || "").toLowerCase();
      if (r.endsWith("sub warden")) return 1;
      if (r.endsWith("warden")) return 0;
      return 2;
    };
    return staff.filter(s => s.dept === "Hostel" && (s.role || "").startsWith(rolePrefix)).sort((a, b) => rank(a.role) - rank(b.role) || (a.name || "").localeCompare(b.name || ""));
  }, [staff]);
  const hostelWarden = useMemo(() => hostelStaff.find(s => (s.role || "").toLowerCase().endsWith("warden") && !(s.role || "").toLowerCase().endsWith("sub warden")), [hostelStaff]);

  // Distinct academic years present in the saved students + the generated
  // dropdown range, so the filter always covers real data even if older
  // records used a year outside the default range.
  const regYearOptions = useMemo(() => {
    const fromData = students.map(s => s.academicYear).filter(Boolean);
    return Array.from(new Set([...academicYearOptions(), ...fromData])).sort();
  }, [students]);
  const registeredStudentsTable = useMemo(() => {
    const list = regYearFilter === "All" ? students : students.filter(s => s.academicYear === regYearFilter);
    return [...list].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [students, regYearFilter]);
  const filteredRequests = useMemo(() => {
    if (statusFilter === "All") return requests;
    return requests.filter(r => r.status === statusFilter);
  }, [requests, statusFilter]);
  const reqStats = useMemo(() => {
    const pending = requests.filter(r => r.status === "Pending").length;
    const approved = requests.filter(r => r.status === "Approved").length;
    const today = todayStr();
    const outNow = requests.filter(r => {
      if (r.status !== "Approved" || r.returned) return false;
      if (r.type === "Leave") return r.fromDate <= today && today <= r.toDate;
      if (r.type === "Permission") return r.date === today;
      return false;
    }).length;
    return {
      total: requests.length,
      pending,
      approved,
      outNow
    };
  }, [requests]);
  const complaintStats = useMemo(() => {
    const pending = complaints.filter(c => c.status === "Pending").length;
    const inProgress = complaints.filter(c => c.status === "In Progress").length;
    const resolved = complaints.filter(c => c.status === "Resolved").length;
    return {
      total: complaints.length,
      pending,
      inProgress,
      resolved
    };
  }, [complaints]);
  const filteredComplaints = useMemo(() => {
    if (complaintFilter === "All") return complaints;
    return complaints.filter(c => c.status === complaintFilter);
  }, [complaints, complaintFilter]);
  const feeSummaryTotals = useMemo(() => {
    const paid = feeSummary.reduce((s, r) => s + (r.totalPaid || 0), 0);
    const pending = feeSummary.reduce((s, r) => s + (r.totalPending || 0), 0);
    return {
      paid,
      pending,
      count: feeSummary.length
    };
  }, [feeSummary]);

  // ── Requests logic (persisted via /api/hostel/:hostelType/requests) ────
  // Requests are raised by parents from the Parent Portal's "Leave Request"
  // button (see ParentsDashboard.js) — the Warden here only reviews them:
  // Approve / Reject a Pending request, and mark an Approved one Returned.
  function normalizeRequest(r) {
    return {
      id: r._id,
      studentId: r.studentId || "",
      studentName: r.studentName,
      className: r.className,
      section: r.section || "",
      parentName: r.parentName || "Parent",
      parentMobile: r.parentMobile || "",
      type: r.type,
      reason: r.reason,
      fromDate: r.fromDate || "",
      toDate: r.toDate || "",
      fromTime: r.fromTime || "",
      toTime: r.toTime || "",
      date: r.date || "",
      outTime: r.outTime || "",
      expectedInTime: r.expectedInTime || "",
      destination: r.destination || "",
      status: r.status,
      wardenRemarks: r.wardenRemarks || "",
      returned: !!r.returned,
      createdAt: r.createdAt
    };
  }
  async function decide(id, status) {
    const prevRequests = requests;
    setRequests(prev => prev.map(r => r.id === id ? {
      ...r,
      status
    } : r)); // optimistic update
    try {
      const res = await fetch(`${API_BASE}/${HOSTEL_TYPE}/requests/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          status
        })
      });
      if (!res.ok) throw new Error("Failed to update status");
      const saved = await res.json();
      setRequests(prev => prev.map(r => r.id === id ? normalizeRequest(saved) : r));
    } catch (err) {
      console.error("decide (request status) error:", err);
      setRequests(prevRequests); // roll back on failure
      alert(" Could not update the request. Please check your connection and try again.");
    }
  }
  async function toggleReturned(id, current) {
    const prevRequests = requests;
    setRequests(prev => prev.map(r => r.id === id ? {
      ...r,
      returned: !current
    } : r)); // optimistic update
    try {
      const res = await fetch(`${API_BASE}/${HOSTEL_TYPE}/requests/${id}/returned`, {
        method: "PATCH"
      });
      if (!res.ok) throw new Error("Failed to update returned status");
      const saved = await res.json();
      setRequests(prev => prev.map(r => r.id === id ? normalizeRequest(saved) : r));
    } catch (err) {
      console.error("toggleReturned error:", err);
      setRequests(prevRequests); // roll back on failure
      alert(" Could not update the request. Please check your connection and try again.");
    }
  }

  // ── Block / Floor management ─────────────────────────────────────────
  function selectBlock(id) {
    setActiveBlockId(id);
    const b = blocks.find(x => x.id === id);
    setActiveFloorId(b?.floors[0]?.id || "");
    setEditingBlockName(false);
    setEditingFloorClasses(false);
    setEditingRoomId("");
    setEditingWashroomId("");
  }
  function startEditBlockName() {
    setBlockNameDraft(activeBlock?.name || "");
    setEditingBlockName(true);
  }
  function saveBlockName() {
    if (!blockNameDraft.trim()) {
      setEditingBlockName(false);
      return;
    }
    const nextRenamed = blocks.map(b => b.id === activeBlockId ? {
      ...b,
      name: blockNameDraft.trim()
    } : b);
    saveStructure(nextRenamed, rules);
    setEditingBlockName(false);
  }
  function addBlock() {
    if (!newBlockName.trim()) return;
    const next = [...blocks, {
      name: newBlockName.trim(),
      floors: [{
        floorNo: 1,
        classes: [],
        rooms: [],
        washrooms: genWashrooms("N", 1)
      }]
    }];
    saveStructure(next, rules).then(norm => {
      if (!norm) return;
      const created = norm.blocks[norm.blocks.length - 1];
      if (created) {
        setActiveBlockId(created.id);
        setActiveFloorId(created.floors[0]?.id || "");
      }
    });
    setNewBlockName("");
  }
  function deleteBlock(id) {
    const b = blocks.find(x => x.id === id);
    const hasStudents = b?.floors.some(f => f.rooms.some(r => (occupancyByRoom[r.id] || 0) > 0));
    if (hasStudents) {
      alert("Cannot delete a block that has students assigned. Reassign them first.");
      return;
    }
    if (blocks.length <= 1) {
      alert("At least one block must remain.");
      return;
    }
    const remaining = blocks.filter(x => x.id !== id);
    saveStructure(remaining, rules).then(norm => {
      if (!norm) return;
      if (activeBlockId === id) {
        setActiveBlockId(remaining[0].id);
        setActiveFloorId(remaining[0].floors[0]?.id || "");
      }
    });
  }
  function addFloor() {
    const classes = newFloorClasses.split(",").map(c => c.trim()).filter(Boolean);
    const nextFloorNo = (activeBlock?.floors.length || 0) + 1;
    const prefix = activeBlock?.name?.[0]?.toUpperCase() || "F";
    const next = blocks.map(b => b.id !== activeBlockId ? b : {
      ...b,
      floors: [...b.floors, {
        floorNo: nextFloorNo,
        classes,
        rooms: genRooms(prefix, nextFloorNo, 10),
        washrooms: genWashrooms(prefix, nextFloorNo)
      }]
    });
    saveStructure(next, rules).then(norm => {
      if (!norm) return;
      const nb = norm.blocks.find(b => b.id === activeBlockId);
      const nf = nb?.floors[nb.floors.length - 1];
      if (nf) setActiveFloorId(nf.id);
    });
    setNewFloorClasses("");
  }
  function deleteFloor(floorId) {
    const floor = activeBlock?.floors.find(f => f.id === floorId);
    const hasStudents = floor?.rooms.some(r => (occupancyByRoom[r.id] || 0) > 0);
    if (hasStudents) {
      alert("Cannot delete a floor that has students assigned. Reassign them first.");
      return;
    }
    if ((activeBlock?.floors.length || 0) <= 1) {
      alert("At least one floor must remain in a block.");
      return;
    }
    const next = blocks.map(b => b.id !== activeBlockId ? b : {
      ...b,
      floors: b.floors.filter(f => f.id !== floorId)
    });
    saveStructure(next, rules).then(norm => {
      if (!norm) return;
      if (activeFloorId === floorId) {
        const remaining = activeBlock.floors.filter(f => f.id !== floorId);
        setActiveFloorId(remaining[0]?.id || "");
      }
    });
  }
  function updateFloorClasses(floorId, value) {
    const classes = value.split(",").map(c => c.trim()).filter(Boolean);
    const next = blocks.map(b => b.id !== activeBlockId ? b : {
      ...b,
      floors: b.floors.map(f => f.id === floorId ? {
        ...f,
        classes
      } : f)
    });
    saveStructure(next, rules);
  }
  function startEditFloorClasses() {
    setFloorClassesDraft((activeFloor?.classes || []).join(", "));
    setEditingFloorClasses(true);
  }
  function saveFloorClasses() {
    updateFloorClasses(activeFloor.id, floorClassesDraft);
    setEditingFloorClasses(false);
  }
  function cancelEditFloorClasses() {
    setEditingFloorClasses(false);
  }

  // ── Room / Washroom explicit edit mode (Edit -> Save/Cancel, like Block Rename) ──
  function startEditRoom(r) {
    setEditingRoomId(r.id);
    setRoomDraft({
      roomNo: r.roomNo,
      capacity: String(r.capacity),
      notes: r.notes || ""
    });
  }
  function cancelEditRoom() {
    setEditingRoomId("");
  }
  function saveRoomEdit(roomId) {
    const cap = Math.max(1, Number(roomDraft.capacity) || 1);
    const next = blocks.map(b => b.id !== activeBlockId ? b : {
      ...b,
      floors: b.floors.map(f => f.id !== activeFloorId ? f : {
        ...f,
        rooms: f.rooms.map(r => r.id === roomId ? {
          ...r,
          roomNo: roomDraft.roomNo.trim() || r.roomNo,
          capacity: cap,
          notes: roomDraft.notes
        } : r)
      })
    });
    saveStructure(next, rules);
    setEditingRoomId("");
  }
  function startEditWashroom(w) {
    setEditingWashroomId(w.id);
    setWashroomDraft({
      label: w.label,
      showers: String(w.showers),
      lights: String(w.lights),
      washbasins: String(w.washbasins)
    });
  }
  function cancelEditWashroom() {
    setEditingWashroomId("");
  }
  function saveWashroomEdit(wrId) {
    const next = blocks.map(b => b.id !== activeBlockId ? b : {
      ...b,
      floors: b.floors.map(f => f.id !== activeFloorId ? f : {
        ...f,
        washrooms: f.washrooms.map(w => w.id === wrId ? {
          ...w,
          label: washroomDraft.label.trim() || w.label,
          showers: Math.max(0, Number(washroomDraft.showers) || 0),
          lights: Math.max(0, Number(washroomDraft.lights) || 0),
          washbasins: Math.max(0, Number(washroomDraft.washbasins) || 0)
        } : w)
      })
    });
    saveStructure(next, rules);
    setEditingWashroomId("");
  }

  // ── Room management (inside active floor) ────────────────────────────
  function addRoom() {
    setRoomError("");
    if (!roomForm.roomNo.trim()) {
      setRoomError("Room number is required.");
      return;
    }
    if (activeFloor?.rooms.some(r => r.roomNo === roomForm.roomNo.trim())) {
      setRoomError(`Room ${roomForm.roomNo} already exists on this floor.`);
      return;
    }
    const cap = Number(roomForm.capacity);
    if (!cap || cap < 1) {
      setRoomError("Capacity must be at least 1.");
      return;
    }
    const newRoom = {
      roomNo: roomForm.roomNo.trim(),
      capacity: cap,
      notes: roomForm.notes.trim()
    };
    const next = blocks.map(b => b.id !== activeBlockId ? b : {
      ...b,
      floors: b.floors.map(f => f.id !== activeFloorId ? f : {
        ...f,
        rooms: [...f.rooms, newRoom]
      })
    });
    saveStructure(next, rules).then(norm => {
      if (!norm) {
        setRoomError(" Could not save the room. Please try again.");
        return;
      }
      setRoomForm(emptyRoomForm);
    });
  }
  function updateRoom(roomId, field, value) {
    const next = blocks.map(b => b.id !== activeBlockId ? b : {
      ...b,
      floors: b.floors.map(f => f.id !== activeFloorId ? f : {
        ...f,
        rooms: f.rooms.map(r => r.id === roomId ? {
          ...r,
          [field]: field === "capacity" ? Math.max(1, Number(value) || 1) : value
        } : r)
      })
    });
    scheduleStructureSave(next, rules);
  }
  function deleteRoom(roomId) {
    const occ = occupancyByRoom[roomId] || 0;
    if (occ > 0) {
      alert("Cannot delete a room that currently has students assigned. Reassign them first.");
      return;
    }
    const next = blocks.map(b => b.id !== activeBlockId ? b : {
      ...b,
      floors: b.floors.map(f => f.id !== activeFloorId ? f : {
        ...f,
        rooms: f.rooms.filter(r => r.id !== roomId)
      })
    });
    saveStructure(next, rules);
  }

  // ── Washroom management (inside active floor) ────────────────────────
  function addWashroom() {
    setWashroomError("");
    if (!washroomForm.label.trim()) {
      setWashroomError("Washroom label is required.");
      return;
    }
    const newWashroom = {
      label: washroomForm.label.trim(),
      showers: Math.max(0, Number(washroomForm.showers) || 0),
      lights: Math.max(0, Number(washroomForm.lights) || 0),
      washbasins: Math.max(0, Number(washroomForm.washbasins) || 0)
    };
    const next = blocks.map(b => b.id !== activeBlockId ? b : {
      ...b,
      floors: b.floors.map(f => f.id !== activeFloorId ? f : {
        ...f,
        washrooms: [...f.washrooms, newWashroom]
      })
    });
    saveStructure(next, rules).then(norm => {
      if (!norm) {
        setWashroomError(" Could not save the washroom. Please try again.");
        return;
      }
      setWashroomForm(emptyWashroomForm);
    });
  }
  function updateWashroom(wrId, field, value) {
    const next = blocks.map(b => b.id !== activeBlockId ? b : {
      ...b,
      floors: b.floors.map(f => f.id !== activeFloorId ? f : {
        ...f,
        washrooms: f.washrooms.map(w => w.id === wrId ? {
          ...w,
          [field]: field === "label" ? value : Math.max(0, Number(value) || 0)
        } : w)
      })
    });
    scheduleStructureSave(next, rules);
  }
  function deleteWashroom(wrId) {
    const next = blocks.map(b => b.id !== activeBlockId ? b : {
      ...b,
      floors: b.floors.map(f => f.id !== activeFloorId ? f : {
        ...f,
        washrooms: f.washrooms.filter(w => w.id !== wrId)
      })
    });
    saveStructure(next, rules);
  }
  function addRule() {
    if (!newRule.trim()) return;
    const next = [...rules, newRule.trim()];
    saveStructure(blocks, next);
    setNewRule("");
  }
  function printRules() {
    const win = window.open("", "_blank", "width=800,height=900");
    if (!win) return;
    const rows = rules.map(r => `<li>${r.replace(/</g, "&lt;")}</li>`).join("");
    win.document.write(`
      <html>
        <head>
          <title>${HOSTEL_NAME} — Common Hostel Rules</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #1A1A1A; }
            h1 { color: ${NAVY}; font-size: 20px; margin-bottom: 2px; }
 .sub { color: #607D8B; font-size: 12px; margin-bottom: 24px; }
 ul { padding-left: 20px; }
 li { font-size: 14px; line-height: 1.9; margin-bottom: 6px; }
 </style>
 </head>
 <body>
 <h1>${HOSTEL_NAME} — Common Hostel Rules</h1>
          <div class="sub">Applies to the whole ${HOSTEL_NAME}</div>
          <ul>${rows || "<li>No rules added yet.</li>"}</ul>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
  }
  function removeRule(idx) {
    const next = rules.filter((_, i) => i !== idx);
    saveStructure(blocks, next);
  }

  // ── Student registration ──────────────────────────────────────────────
  function handleClassChange(value) {
    setRegForm(f => ({
      ...f,
      className: value
    }));
    const match = findMatchingFloor(blocks, value);
    if (match) {
      setRegForm(f => ({
        ...f,
        className: value,
        blockId: match.blockId,
        floorId: match.floorId,
        roomId: ""
      }));
      setRegAutoMatched(true);
    } else {
      setRegAutoMatched(false);
    }
  }
  function regBlockOptions() {
    return blocks;
  }
  function regFloorOptions() {
    const b = blocks.find(x => x.id === regForm.blockId);
    return b?.floors || [];
  }
  function regRoomOptions() {
    const b = blocks.find(x => x.id === regForm.blockId);
    const f = b?.floors.find(x => x.id === regForm.floorId);
    return f?.rooms || [];
  }
  async function submitRegistration() {
    setRegError("");
    setRegSuccess("");
    if (!regForm.name.trim()) {
      setRegError("Student name is required.");
      return;
    }
    if (!regForm.address.trim()) {
      setRegError("Address is required.");
      return;
    }
    if (!regForm.parentNumber.trim() || regForm.parentNumber.trim().length < 10) {
      setRegError("Please enter a valid parent number.");
      return;
    }
    if (!regForm.className.trim()) {
      setRegError("Class is required.");
      return;
    }
    if (!regForm.section.trim()) {
      setRegError("Section is required.");
      return;
    }
    if (!regForm.academicYear.trim()) {
      setRegError("Please select the Academic Year / Batch.");
      return;
    }
    if (!regForm.blockId || !regForm.floorId) {
      setRegError("Please select the Block and Floor.");
      return;
    }
    if (!regForm.roomId) {
      setRegError("Please select a room to allot.");
      return;
    }
    const room = regRoomOptions().find(r => r.id === regForm.roomId);
    const occ = occupancyByRoom[regForm.roomId] || 0;
    if (room && occ >= room.capacity) {
      setRegError(`Room ${room.roomNo} is already full.`);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/${HOSTEL_TYPE}/students`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: regForm.name.trim(),
          address: regForm.address.trim(),
          parentNumber: regForm.parentNumber.trim(),
          altNumber: regForm.altNumber.trim(),
          schoolName: regForm.schoolName.trim(),
          className: regForm.className.trim(),
          section: regForm.section.trim(),
          academicYear: regForm.academicYear.trim(),
          gender: GENDER,
          blockId: regForm.blockId,
          floorId: regForm.floorId,
          roomId: regForm.roomId
        })
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        setRegError(errBody.message || "Failed to register student. Please try again.");
        return;
      }
      const saved = await res.json();
      const newStudent = {
        id: saved._id,
        name: saved.name,
        address: saved.address,
        parentNumber: saved.parentNumber,
        altNumber: saved.altNumber,
        schoolName: saved.schoolName,
        className: saved.className,
        section: saved.section,
        academicYear: saved.academicYear,
        gender: saved.gender,
        roomId: saved.roomId,
        createdAt: saved.createdAt
      };
      setStudents(prev => [newStudent, ...prev]);
      setRegSuccess(`${newStudent.name} registered and allotted Room ${room?.roomNo}.`);
      setRegYearFilter(newStudent.academicYear || "All");
      setRegForm({
        ...emptyRegForm,
        academicYear: regForm.academicYear
      });
      setRegAutoMatched(false);
    } catch (err) {
      console.error("submitRegistration error:", err);
      setRegError(" Network error — could not reach the server. Please try again.");
    }
  }

  // ── Registered Students table: edit a student's details / room ────────
  function startEditStudent(s) {
    const meta = findRoomMeta(blocks, s.roomId);
    setEditingStudentId(s.id);
    setStudentEditError("");
    setStudentEditForm({
      name: s.name,
      address: s.address,
      parentNumber: s.parentNumber,
      altNumber: s.altNumber || "",
      schoolName: s.schoolName || "",
      className: s.className,
      section: s.section,
      academicYear: s.academicYear || currentAcademicYear(),
      blockId: meta?.blockId || "",
      floorId: meta?.floorId || "",
      roomId: s.roomId || ""
    });
  }
  function cancelEditStudent() {
    setEditingStudentId("");
    setStudentEditForm(null);
    setStudentEditError("");
  }
  function studentEditFloorOptions() {
    const b = blocks.find(x => x.id === studentEditForm?.blockId);
    return b?.floors || [];
  }
  function studentEditRoomOptions() {
    const b = blocks.find(x => x.id === studentEditForm?.blockId);
    const f = b?.floors.find(x => x.id === studentEditForm?.floorId);
    return f?.rooms || [];
  }
  async function saveStudentEdit(studentId) {
    setStudentEditError("");
    if (!studentEditForm.name.trim()) {
      setStudentEditError("Student name is required.");
      return;
    }
    if (!studentEditForm.address.trim()) {
      setStudentEditError("Address is required.");
      return;
    }
    if (!studentEditForm.parentNumber.trim() || studentEditForm.parentNumber.trim().length < 10) {
      setStudentEditError("Please enter a valid parent number.");
      return;
    }
    if (!studentEditForm.className.trim()) {
      setStudentEditError("Class is required.");
      return;
    }
    if (!studentEditForm.section.trim()) {
      setStudentEditError("Section is required.");
      return;
    }
    if (!studentEditForm.academicYear.trim()) {
      setStudentEditError("Academic Year is required.");
      return;
    }
    if (!studentEditForm.blockId || !studentEditForm.floorId) {
      setStudentEditError("Please select the Block and Floor.");
      return;
    }
    if (!studentEditForm.roomId) {
      setStudentEditError("Please select a room to allot.");
      return;
    }
    const room = studentEditRoomOptions().find(r => r.id === studentEditForm.roomId);
    const occ = students.filter(s => s.roomId === studentEditForm.roomId && s.id !== studentId).length;
    if (room && occ >= room.capacity) {
      setStudentEditError(`Room ${room.roomNo} is already full.`);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/${HOSTEL_TYPE}/students/${studentId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: studentEditForm.name.trim(),
          address: studentEditForm.address.trim(),
          parentNumber: studentEditForm.parentNumber.trim(),
          altNumber: studentEditForm.altNumber.trim(),
          schoolName: studentEditForm.schoolName.trim(),
          className: studentEditForm.className.trim(),
          section: studentEditForm.section.trim(),
          academicYear: studentEditForm.academicYear.trim(),
          gender: GENDER,
          blockId: studentEditForm.blockId,
          floorId: studentEditForm.floorId,
          roomId: studentEditForm.roomId
        })
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        setStudentEditError(errBody.message || "Failed to update student. Please try again.");
        return;
      }
      const saved = await res.json();
      setStudents(prev => prev.map(s => s.id === studentId ? {
        id: saved._id,
        name: saved.name,
        address: saved.address,
        parentNumber: saved.parentNumber,
        altNumber: saved.altNumber,
        schoolName: saved.schoolName,
        className: saved.className,
        section: saved.section,
        academicYear: saved.academicYear,
        gender: saved.gender,
        roomId: saved.roomId,
        createdAt: saved.createdAt
      } : s));
      setEditingStudentId("");
      setStudentEditForm(null);
    } catch (err) {
      console.error("saveStudentEdit error:", err);
      setStudentEditError(" Network error — could not reach the server. Please try again.");
    }
  }

  // ── Complaints logic (persisted via /api/hostel/:hostelType/complaints) ─
  function normalizeComplaint(c) {
    return {
      id: c._id,
      studentId: c.studentId,
      category: c.category,
      description: c.description,
      status: c.status,
      assignedTo: c.assignedTo || "",
      assignedWorkerId: c.assignedWorkerId || "",
      createdAt: c.createdAt
    };
  }
  async function submitComplaint() {
    setComplaintError("");
    if (!complaintForm.studentId) {
      setComplaintError("Please select a student.");
      return;
    }
    if (!complaintForm.description.trim()) {
      setComplaintError("Please describe the issue.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/${HOSTEL_TYPE}/complaints`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          studentId: complaintForm.studentId,
          category: complaintForm.category,
          description: complaintForm.description.trim()
        })
      });
      if (!res.ok) {
        setComplaintError(" Failed to submit complaint. Please try again.");
        return;
      }
      const saved = await res.json();
      setComplaints(prev => [normalizeComplaint(saved), ...prev]);
      setComplaintForm(emptyComplaintForm);
    } catch (err) {
      console.error("submitComplaint error:", err);
      setComplaintError(" Network error — could not reach the server. Please try again.");
    }
  }
  async function updateComplaintStatus(id, status) {
    const prevComplaints = complaints;
    setComplaints(prev => prev.map(c => c.id === id ? {
      ...c,
      status
    } : c)); // optimistic update
    try {
      const res = await fetch(`${API_BASE}/${HOSTEL_TYPE}/complaints/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          status
        })
      });
      if (!res.ok) throw new Error("Failed to update status");
      const saved = await res.json();
      setComplaints(prev => prev.map(c => c.id === id ? normalizeComplaint(saved) : c));
    } catch (err) {
      console.error("updateComplaintStatus error:", err);
      setComplaints(prevComplaints); // roll back on failure
      alert(" Could not update complaint status. Please check your connection and try again.");
    }
  }

  // Returns the active workers whose role matches the given complaint
  // category (e.g. Electrical -> Electrician). Falls back to all active
  // workers if nothing matches, so a worker can still be assigned.
  function getMatchingWorkers(category) {
    const keywords = CATEGORY_ROLE_MAP[category] || [];
    const activeWorkers = workers.filter(w => w.status === "Active");
    if (keywords.length === 0) return activeWorkers;
    const matched = activeWorkers.filter(w => keywords.some(k => (w.role || "").toLowerCase().includes(k)));
    return matched.length > 0 ? matched : activeWorkers;
  }

  // Sends the complaint details to the assigned worker's WhatsApp number,
  // combining the school name + this hostel's Warden contact so the worker
  // knows exactly who raised it and where to report back.
  function sendComplaintToWorkerWhatsApp(complaint, worker) {
    let phone = (worker.phone || "").replace(/[^+\d]/g, "");
    if (!phone) {
      alert(` No phone number on file for ${worker.name}. Could not send WhatsApp message.`);
      return;
    }
    if (phone.length === 10) phone = "91" + phone; // default to India country code if none given

    const student = studentById(complaint.studentId);
    const meta = findRoomMeta(blocks, student?.roomId);
    const warden = hostelWarden;
    const message = ` *${SCHOOL_NAME()}*
${HOSTEL_NAME} — Maintenance Complaint Assigned

*Assigned To:* ${worker.name} (${worker.role})
${CATEGORY_ICON[complaint.category] || <Wrench className="inline w-[1em] h-[1em] align-[-0.125em]" />} *Category:* ${complaint.category}
*Student:* ${student?.name || "Unknown"}${meta ? `
*Location:* ${meta.blockName} · Room ${meta.roomNo}` : ""}

*Issue:*
${complaint.description}

Please attend to this at the earliest and update the ${WARDEN_LABEL} once resolved.${warden ? `

*Reported by:* ${warden.name} (${WARDEN_LABEL})
*Warden Contact:* ${warden.phone}` : ""}

_This is a system-generated message from ${SCHOOL_NAME()}._`;
    const encodedMsg = encodeURIComponent(message);
    const waUrl = `https://wa.me/${phone}?text=${encodedMsg}`;
    const newWindow = window.open(waUrl, "_blank");
    if (!newWindow || newWindow.closed) {
      window.location.href = waUrl;
    }
  }
  async function assignStaffToComplaint(id) {
    const workerId = assignInputs[id] || "";
    if (!workerId) return;
    const worker = workers.find(w => w._id === workerId);
    if (!worker) return;
    const complaint = complaints.find(c => c.id === id);
    try {
      const res = await fetch(`${API_BASE}/${HOSTEL_TYPE}/complaints/${id}/assign`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          assignedWorkerId: worker._id,
          assignedTo: worker.name
        })
      });
      if (!res.ok) throw new Error("Failed to assign worker");
      const saved = await res.json();
      setComplaints(prev => prev.map(c => c.id === id ? normalizeComplaint(saved) : c));
      setAssignInputs(prev => ({
        ...prev,
        [id]: ""
      }));
      if (complaint) sendComplaintToWorkerWhatsApp(complaint, worker);
    } catch (err) {
      console.error("assignStaffToComplaint error:", err);
      alert(" Could not assign worker. Please check your connection and try again.");
    }
  }

  // ── Fee logic (read-only view; data comes from /fee-summary) ───────────
  async function loadFeeSummary() {
    setFeeLoading(true);
    setFeeLoadError("");
    try {
      const res = await fetch(`${API_BASE}/${HOSTEL_TYPE}/fee-summary`);
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      setFeeSummary(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("loadFeeSummary error:", err);
      setFeeLoadError("Could not load fee details. Please check your connection and try again.");
    } finally {
      setFeeLoading(false);
    }
  }

  // ── Mess menu logic ───────────────────────────────────────────────────
  function updateMenu(day, meal, value) {
    setMessMenu(prev => {
      const next = {
        ...prev,
        [day]: {
          ...prev[day],
          [meal]: value
        }
      };
      if (messMenuSaveTimer.current) clearTimeout(messMenuSaveTimer.current);
      messMenuSaveTimer.current = setTimeout(() => saveMessMenuToServer(next), 600);
      return next;
    });
  }

  // Staff tab is read-only (see hostelStaff above) — no add/remove here.
  // Wardens are created/edited from Staff Management → "Add Warden".

  // ── Inventory logic ───────────────────────────────────────────────────
  // Edits are staged in `inventoryDraft` while a row is in edit mode, and
  // only sent to the server (and reflected in `roomInventory`) once the
  // user explicitly presses Save. Nothing is auto-saved while typing.
  function startEditInventory(roomId) {
    setInventoryError("");
    setEditingInventoryRoomId(roomId);
    setInventoryDraft({
      ...blankInventory(),
      ...(roomInventory[roomId] || {})
    });
  }
  function updateInventoryDraft(itemKey, value) {
    const num = Math.max(0, Math.floor(Number(value) || 0));
    setInventoryDraft(prev => ({
      ...(prev || blankInventory()),
      [itemKey]: num
    }));
  }
  function cancelEditInventory() {
    setEditingInventoryRoomId("");
    setInventoryDraft(null);
  }
  async function saveEditInventory(roomId) {
    const counts = inventoryDraft || blankInventory();
    setInventorySavingRoomId(roomId);
    const ok = await saveInventoryToServer(roomId, counts);
    setInventorySavingRoomId("");
    if (ok) {
      setRoomInventory(prev => ({
        ...prev,
        [roomId]: counts
      }));
      setEditingInventoryRoomId("");
      setInventoryDraft(null);
    }
  }
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("role");
    navigate("/hostel-login");
  };

  // ── Messaging logic (Warden -> Principal, this hostel only) ─────────────
  // This page belongs to the {HOSTEL_TYPE} Warden, so every message sent
  // from here is always tagged "Warden" (scoped to this hostelType). There
  // is no "send as Principal" option on this screen — the Principal replies
  // from their own dashboard (PrincipalView.js), in the matching
  // "Chat with {HOSTEL_TYPE} Warden" panel.
  async function sendMessage() {
    setMessageError("");
    if (!messageForm.body.trim()) {
      setMessageError("Please type a message.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/${HOSTEL_TYPE}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          from: "Warden",
          priority: messageForm.priority,
          body: messageForm.body.trim()
        })
      });
      if (!res.ok) {
        setMessageError(" Failed to send message. Please try again.");
        return;
      }
      const saved = await res.json();
      const newMessage = {
        id: saved._id,
        from: saved.from,
        priority: saved.priority,
        body: saved.body,
        createdAt: saved.createdAt
      };
      setMessages(prev => [...prev, newMessage]);
      setMessageForm(emptyMessageForm);
    } catch (err) {
      console.error("sendMessage error:", err);
      setMessageError(" Network error — could not reach the server. Please try again.");
    }
  }
  return <div style={{
    height: isMobile ? "auto" : "100vh",
    minHeight: "100vh",
    background: PALE,
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    padding: isMobile ? 12 : 24,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflow: isMobile ? "visible" : "hidden"
  }}>
      {/* Header — sticky at the top */}
      <div style={{
      background: `linear-gradient(135deg, ${NAVY}, ${BLUE}, ${LIGHT})`,
      borderRadius: 24,
      padding: "22px 28px",
      marginBottom: 20,
      boxShadow: "0 4px 20px rgba(13,71,161,0.25)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 16,
      position: "sticky",
      top: 0,
      zIndex: 50,
      flexShrink: 0
    }}>
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 16
      }}>
          <div style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: "rgba(255,255,255,0.22)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 28
        }}><User className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
          <div>
            <div style={{
            fontWeight: 800,
            fontSize: 21,
            color: "#FFFFFF",
            letterSpacing: "-0.3px"
          }}>{HOSTEL_NAME} Management</div>
            <div style={{
            fontSize: 11,
            color: "rgba(255,255,255,0.85)",
            letterSpacing: "0.6px",
            textTransform: "uppercase",
            fontWeight: 500
          }}>
              Blocks · Floors · Rooms · Washrooms · Fee · Mess · Complaints · Staff
            </div>
          </div>
        </div>
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 12
      }}>
          <div style={{
          background: "rgba(255,255,255,0.22)",
          borderRadius: 14,
          padding: "10px 20px",
          fontSize: 13,
          fontWeight: 700,
          color: "#FFFFFF"
        }}>
            <User className="inline w-[1em] h-[1em] align-[-0.125em]" /> {HOSTEL_NAME}
          </div>
          <button onClick={handleLogout} style={{
          background: "rgba(255,255,255,0.15)",
          border: "1px solid rgba(255,255,255,0.25)",
          color: "#FFFFFF",
          padding: "10px 20px",
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
          transition: "all 0.2s"
        }} onMouseEnter={e => {
          e.target.style.background = "rgba(255,255,255,0.25)";
        }} onMouseLeave={e => {
          e.target.style.background = "rgba(255,255,255,0.15)";
        }}>
            <span><DoorOpen className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Logout
          </button>
        </div>
      </div>

      {loading && <div style={{
      background: "#FFFFFF",
      border: `1px solid ${LIGHT}`,
      color: NAVY,
      borderRadius: 14,
      padding: "12px 18px",
      marginBottom: 16,
      fontSize: 13,
      fontWeight: 600,
      flexShrink: 0
    }}>
          ⏳ Loading hostel data from the server…
        </div>}
      {loadError && <div style={{
      background: "#FFEBEE",
      border: "1px solid #FFCDD2",
      color: "#C62828",
      borderRadius: 14,
      padding: "12px 18px",
      marginBottom: 16,
      fontSize: 13,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
      flexShrink: 0
    }}>
          <span>{loadError}</span>
          <button onClick={loadAllHostelData} style={{
        background: "#C62828",
        color: "#FFFFFF",
        border: "none",
        padding: "7px 16px",
        borderRadius: 10,
        fontSize: 12,
        fontWeight: 700,
        cursor: "pointer"
      }}>Retry</button>
        </div>}

      {/* Sidebar navigation + content — sidebar runs the full length beside
          the content, and the content column scrolls on its own. */}
      <div style={{
      display: "flex",
      gap: 20,
      alignItems: "stretch",
      flex: isMobile ? "none" : 1,
      minHeight: isMobile ? "auto" : 0
    }}>
        {/* Sidebar */}
        <div style={{
        width: isMobile ? "100%" : 230,
        flexShrink: 0,
        background: "#FFFFFF",
        border: `1px solid ${LIGHT}`,
        borderRadius: 18,
        padding: 10,
        display: "flex",
        flexDirection: isMobile ? "row" : "column",
        gap: 6,
        overflowX: isMobile ? "auto" : "visible",
        overflowY: isMobile ? "visible" : "auto"
      }}>
          {[{
          id: "requests",
          label: "Requests"
        }, {
          id: "rooms",
          label: "Rooms & Washrooms"
        }, {
          id: "register",
          label: "Register Student"
        }, {
          id: "complaints",
          label: " Complaints"
        }, {
          id: "fee",
          label: "Fee"
        }, {
          id: "mess",
          label: " Mess Menu"
        }, {
          id: "staff",
          label: "Staff"
        }, {
          id: "inventory",
          label: "Inventory"
        }, {
          id: "messages",
          label: " Messages"
        }].map(t => <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
          border: "none",
          background: activeTab === t.id ? NAVY : "transparent",
          color: activeTab === t.id ? "#FFFFFF" : NAVY,
          padding: "11px 16px",
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          textAlign: "left",
          whiteSpace: "nowrap",
          flexShrink: 0
        }}>{t.label}</button>)}
        </div>

        {/* Tab content — scrolls independently of the sidebar */}
        <div style={{
        flex: 1,
        minWidth: 0,
        overflowY: isMobile ? "visible" : "auto",
        height: isMobile ? "auto" : "100%",
        paddingRight: isMobile ? 0 : 4
      }}>

      {/* ══════════════ REQUESTS TAB ══════════════ */}
      {activeTab === "requests" && <div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
            marginBottom: 20
          }}>
            <StatCard label="Total Requests" value={reqStats.total} color={NAVY} icon="" />
            <StatCard label="Pending Approval" value={reqStats.pending} color="#E65100" icon="⏳" />
            <StatCard label="Approved" value={reqStats.approved} color="#2E7D32" icon="" />
            <StatCard label="Currently Out" value={reqStats.outNow} color={BLUE} icon="" />
          </div>

          <div style={{
            display: "flex",
            gap: 8,
            marginBottom: 16,
            flexWrap: "wrap"
          }}>
            {["All", "Pending", "Approved", "Rejected"].map(s => <button key={s} onClick={() => setStatusFilter(s)} style={{
              border: `1px solid ${statusFilter === s ? NAVY : LIGHT}`,
              background: statusFilter === s ? PALE : "#FFFFFF",
              color: NAVY,
              padding: "6px 16px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer"
            }}>{s} {s !== "All" ? `(${requests.filter(r => r.status === s).length})` : `(${requests.length})`}</button>)}
          </div>

          {filteredRequests.length === 0 ? <EmptyBox text={`No ${statusFilter !== "All" ? statusFilter.toLowerCase() + " " : ""}requests yet.`} /> : <div style={{
            display: "flex",
            flexDirection: "column",
            gap: 12
          }}>
              {filteredRequests.map(r => {
              const sc = STATUS_COLOR[r.status] || STATUS_COLOR.Pending;
              const tc = TYPE_COLOR[r.type] || TYPE_COLOR.Leave;
              const today = todayStr();
              const isOutNow = r.status === "Approved" && !r.returned && (r.type === "Leave" && r.fromDate <= today && today <= r.toDate || r.type === "Permission" && r.date === today);
              const student = studentById(r.studentId);
              const meta = findRoomMeta(blocks, student?.roomId);
              return <div key={r.id} style={{
                background: "#FFFFFF",
                borderRadius: 18,
                border: `1px solid ${LIGHT}`,
                overflow: "hidden",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                display: "flex"
              }}>
                    <div style={{
                  width: 6,
                  background: tc.bg,
                  flexShrink: 0
                }} />
                    <div style={{
                  padding: "16px 20px",
                  flex: 1
                }}>
                      <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: 10
                  }}>
                        <div>
                          <div style={{
                        fontWeight: 700,
                        fontSize: 15,
                        color: "#1A1A1A"
                      }}>
                            {r.studentName}{" "}
                            <span style={{
                          fontWeight: 500,
                          fontSize: 12,
                          color: "#607D8B"
                        }}>
                              · Class {r.className}{r.section ? `-${r.section}` : ""}{meta ? ` · ${meta.blockName} · Room ${meta.roomNo}` : ""}
                            </span>
                          </div>
                          <div style={{
                        display: "flex",
                        gap: 8,
                        marginTop: 6,
                        alignItems: "center",
                        flexWrap: "wrap"
                      }}>
                            <span style={{
                          background: tc.bg,
                          color: "#FFFFFF",
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "3px 10px",
                          borderRadius: 20
                        }}>{tc.label}</span>
                            <span style={{
                          background: sc.bg,
                          color: sc.text,
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "3px 10px",
                          borderRadius: 20,
                          display: "flex",
                          alignItems: "center",
                          gap: 5
                        }}>
                              <span style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: sc.dot
                          }} /> {r.status}
                            </span>
                            {isOutNow && <span style={{
                          background: "#FFF8E1",
                          color: "#F57F17",
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "3px 10px",
                          borderRadius: 20
                        }}><Footprints className="inline w-[1em] h-[1em] align-[-0.125em]" /> Out now</span>}
                            {r.status === "Approved" && r.returned && <span style={{
                          background: "#E0F2F1",
                          color: "#00695C",
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "3px 10px",
                          borderRadius: 20
                        }}><Home className="inline w-[1em] h-[1em] align-[-0.125em]" /> Returned</span>}
                          </div>
                        </div>
                        <div style={{
                      display: "flex",
                      gap: 8,
                      flexShrink: 0
                    }}>
                          {r.status === "Pending" && <>
                              <button onClick={() => decide(r.id, "Approved")} style={{
                          background: "#2E7D32",
                          color: "#FFFFFF",
                          border: "none",
                          padding: "8px 16px",
                          borderRadius: 10,
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer"
                        }}><Check className="inline w-[1em] h-[1em] align-[-0.125em]" /> Approve</button>
                              <button onClick={() => decide(r.id, "Rejected")} style={{
                          background: "#C62828",
                          color: "#FFFFFF",
                          border: "none",
                          padding: "8px 16px",
                          borderRadius: 10,
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer"
                        }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /> Reject</button>
                            </>}
                          {r.status === "Approved" && <button onClick={() => toggleReturned(r.id, r.returned)} style={{
                        background: r.returned ? "#FFFFFF" : PALE,
                        color: NAVY,
                        border: `1px solid ${NAVY}`,
                        padding: "8px 16px",
                        borderRadius: 10,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer"
                      }}>
                              {r.returned ? "Mark Not Returned" : "Mark Returned"}
                            </button>}
                        </div>
                      </div>

                      <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: 10,
                    marginTop: 14,
                    fontSize: 12.5,
                    color: "#37474F"
                  }}>
                        {r.type === "Leave" ? <>
                            <div><b>From:</b> {fmtDate(r.fromDate)}{r.fromTime ? ` · ${r.fromTime}` : ""}</div>
                            <div><b>To:</b> {fmtDate(r.toDate)}{r.toTime ? ` · ${r.toTime}` : ""}</div>
                          </> : <>
                            <div><b>Date:</b> {fmtDate(r.date)}</div>
                            <div><b>Out:</b> {r.outTime || "—"}</div>
                            <div><b>Expected in:</b> {r.expectedInTime || "—"}</div>
                            <div><b>Destination:</b> {r.destination || "—"}</div>
                          </>}
                        <div><b>Parent:</b> {r.parentMobile || "—"}</div>
                        <div><b>Requested:</b> {fmtDate(r.createdAt)}</div>
                      </div>

                      <div style={{
                    marginTop: 10,
                    fontSize: 13,
                    color: "#1A1A1A",
                    background: PALE,
                    borderRadius: 10,
                    padding: "8px 12px"
                  }}>
                        <b style={{
                      color: NAVY
                    }}>Reason: </b>{r.reason}
                      </div>
                      {r.wardenRemarks && <div style={{
                    marginTop: 8,
                    fontSize: 12.5,
                    color: "#607D8B"
                  }}><b>Warden remarks:</b> {r.wardenRemarks}</div>}
                    </div>
                  </div>;
            })}
            </div>}
        </div>}

      {/* ══════════════ ROOMS & WASHROOMS TAB ══════════════ */}
      {activeTab === "rooms" && <div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
            marginBottom: 20
          }}>
            <StatCard label="Total Rooms" value={roomStats.totalRooms} color={NAVY} icon="" />
            <StatCard label="Total Capacity" value={roomStats.totalCapacity} color={BLUE} icon="" />
            <StatCard label="Occupied" value={roomStats.totalOccupied} color="#E65100" icon="" />
            <StatCard label="Available Beds" value={roomStats.available} color="#2E7D32" icon="" />
          </div>

          {structureError && <div style={{
            background: "#FFEBEE",
            border: "1px solid #FFCDD2",
            color: "#C62828",
            borderRadius: 12,
            padding: "10px 16px",
            marginBottom: 16,
            fontSize: 12.5
          }}>{structureError}</div>}

          {/* Block selector */}
          <div style={{
            display: "flex",
            gap: 8,
            marginBottom: 12,
            flexWrap: "wrap",
            alignItems: "center"
          }}>
            {blocks.map(b => <button key={b.id} onClick={() => selectBlock(b.id)} style={{
              border: `1px solid ${activeBlockId === b.id ? NAVY : LIGHT}`,
              background: activeBlockId === b.id ? NAVY : "#FFFFFF",
              color: activeBlockId === b.id ? "#FFFFFF" : NAVY,
              padding: "9px 18px",
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer"
            }}><Building2 className="inline w-[1em] h-[1em] align-[-0.125em]" /> {b.name}</button>)}
            <input value={newBlockName} onChange={e => setNewBlockName(e.target.value)} placeholder="New block name..." style={{
              ...inputStyle,
              width: 170
            }} />
            <button onClick={addBlock} style={{
              background: NAVY,
              color: "#FFFFFF",
              border: "none",
              padding: "9px 16px",
              borderRadius: 10,
              fontSize: 12.5,
              fontWeight: 700,
              cursor: "pointer"
            }}>+ Add Block</button>
          </div>

          {/* Block name edit + delete */}
          {activeBlock && <div style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 16
          }}>
              {editingBlockName ? <>
                  <input value={blockNameDraft} onChange={e => setBlockNameDraft(e.target.value)} style={{
                ...inputStyle,
                width: 220
              }} autoFocus />
                  <button onClick={saveBlockName} style={{
                background: "#2E7D32",
                color: "#FFFFFF",
                border: "none",
                padding: "8px 14px",
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer"
              }}>Save</button>
                  <button onClick={() => setEditingBlockName(false)} style={{
                background: "#FFFFFF",
                color: NAVY,
                border: `1px solid ${LIGHT}`,
                padding: "8px 14px",
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer"
              }}>Cancel</button>
                </> : <>
                  <div style={{
                fontWeight: 800,
                fontSize: 16,
                color: NAVY
              }}><Building2 className="inline w-[1em] h-[1em] align-[-0.125em]" /> {activeBlock.name}</div>
                  <button onClick={startEditBlockName} style={{
                background: "#FFFFFF",
                color: NAVY,
                border: `1px solid ${LIGHT}`,
                padding: "6px 12px",
                borderRadius: 8,
                fontSize: 11.5,
                fontWeight: 700,
                cursor: "pointer"
              }}><Pencil className="inline w-[1em] h-[1em] align-[-0.125em]" /> Rename</button>
                  <button onClick={() => deleteBlock(activeBlock.id)} style={{
                background: "#FFFFFF",
                color: "#C62828",
                border: `1px solid #FFCDD2`,
                padding: "6px 12px",
                borderRadius: 8,
                fontSize: 11.5,
                fontWeight: 700,
                cursor: "pointer"
              }}><Trash2 className="inline w-[1em] h-[1em] align-[-0.125em]" /> Delete Block</button>
                </>}
            </div>}

          {/* Floor tabs */}
          <div style={{
            display: "flex",
            gap: 8,
            marginBottom: 16,
            flexWrap: "wrap",
            alignItems: "center"
          }}>
            {activeBlock?.floors.map(f => <button key={f.id} onClick={() => {
              setActiveFloorId(f.id);
              setEditingFloorClasses(false);
              setEditingRoomId("");
              setEditingWashroomId("");
            }} style={{
              border: `1px solid ${activeFloorId === f.id ? BLUE : LIGHT}`,
              background: activeFloorId === f.id ? PALE : "#FFFFFF",
              color: NAVY,
              padding: "8px 16px",
              borderRadius: 20,
              fontSize: 12.5,
              fontWeight: 700,
              cursor: "pointer"
            }}>Floor {f.floorNo}{f.classes.length ? ` (Class ${f.classes.join(", ")})` : " (no class set)"}</button>)}
            <input value={newFloorClasses} onChange={e => setNewFloorClasses(e.target.value)} placeholder="New floor classes e.g. 6, 7" style={{
              ...inputStyle,
              width: 190
            }} />
            <button onClick={addFloor} style={{
              background: NAVY,
              color: "#FFFFFF",
              border: "none",
              padding: "8px 16px",
              borderRadius: 10,
              fontSize: 12.5,
              fontWeight: 700,
              cursor: "pointer"
            }}>+ Add Floor</button>
          </div>

          {activeFloor && <>
              {/* Floor class range editor */}
              <div style={{
              background: "#FFFFFF",
              borderRadius: 16,
              border: `1px solid ${LIGHT}`,
              padding: "14px 18px",
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap"
            }}>
                <div style={{
                flex: editingFloorClasses ? "0 0 auto" : 1
              }}>
                  <div style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: NAVY
                }}>Floor {activeFloor.floorNo} — Classes allotted here</div>
                  <div style={{
                  fontSize: 11.5,
                  color: "#607D8B"
                }}>Comma-separated class list. Register Student auto-matches by this.</div>
                </div>
                {editingFloorClasses ? <>
                    <input value={floorClassesDraft} onChange={e => setFloorClassesDraft(e.target.value)} placeholder="e.g. 1, 2, 3" autoFocus style={{
                  ...inputStyle,
                  flex: 1,
                  minWidth: 200
                }} />
                    <button onClick={saveFloorClasses} style={{
                  background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
                  color: "#FFFFFF",
                  border: "none",
                  padding: "9px 16px",
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer"
                }}><Save className="inline w-[1em] h-[1em] align-[-0.125em]" /> Save</button>
                    <button onClick={cancelEditFloorClasses} style={{
                  background: "#FFFFFF",
                  color: "#607D8B",
                  border: `1px solid ${LIGHT}`,
                  padding: "9px 14px",
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer"
                }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /> Cancel</button>
                  </> : <>
                    <div style={{
                  flex: 1,
                  fontSize: 13,
                  color: "#37474F",
                  background: PALE,
                  borderRadius: 10,
                  padding: "9px 14px",
                  minWidth: 200
                }}>
                      {activeFloor.classes.length ? `Class ${activeFloor.classes.join(", ")}` : "— No classes set —"}
                    </div>
                    <button onClick={startEditFloorClasses} style={{
                  background: "#FFFFFF",
                  color: NAVY,
                  border: `1px solid ${LIGHT}`,
                  padding: "9px 14px",
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer"
                }}><Pencil className="inline w-[1em] h-[1em] align-[-0.125em]" /> Edit</button>
                  </>}
                <button onClick={() => deleteFloor(activeFloor.id)} style={{
                background: "#FFFFFF",
                color: "#C62828",
                border: `1px solid #FFCDD2`,
                padding: "9px 14px",
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer"
              }}><Trash2 className="inline w-[1em] h-[1em] align-[-0.125em]" /> Delete Floor</button>
              </div>

              <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1.3fr 1fr",
              gap: 20,
              alignItems: "start"
            }}>
                {/* Rooms on this floor */}
                <div>
                  <div style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: NAVY,
                  marginBottom: 10
                }}><DoorOpen className="inline w-[1em] h-[1em] align-[-0.125em]" /> Rooms on Floor {activeFloor.floorNo}</div>
                  <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  marginBottom: 16
                }}>
                    {activeFloor.rooms.length === 0 ? <EmptyBox text="No rooms on this floor yet." /> : activeFloor.rooms.map(r => {
                    const occ = occupancyByRoom[r.id] || 0;
                    const full = occ >= r.capacity;
                    const occupants = students.filter(s => s.roomId === r.id);
                    const isEditing = editingRoomId === r.id;
                    return <div key={r.id} style={{
                      background: "#FFFFFF",
                      borderRadius: 14,
                      border: `1px solid ${isEditing ? BLUE : LIGHT}`,
                      padding: "12px 16px"
                    }}>
                          {isEditing ? <>
                              <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          flexWrap: "wrap"
                        }}>
                                <input value={roomDraft.roomNo} onChange={e => setRoomDraft(d => ({
                            ...d,
                            roomNo: e.target.value
                          }))} style={{
                            ...inputStyle,
                            width: 100,
                            fontWeight: 700,
                            color: NAVY,
                            padding: "6px 10px"
                          }} autoFocus />
                                <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6
                          }}>
                                  <span style={{
                              fontSize: 11.5,
                              color: "#607D8B"
                            }}>Capacity</span>
                                  <input type="number" min="1" value={roomDraft.capacity} onChange={e => setRoomDraft(d => ({
                              ...d,
                              capacity: e.target.value
                            }))} style={{
                              ...inputStyle,
                              width: 60,
                              padding: "6px 8px",
                              textAlign: "center"
                            }} />
                                </div>
                                <span style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 20,
                            background: full ? "#FFEBEE" : "#E8F5E9",
                            color: full ? "#C62828" : "#2E7D32"
                          }}>{occ}/{r.capacity} {full ? "FULL" : ""}</span>
                              </div>
                              <input value={roomDraft.notes} onChange={e => setRoomDraft(d => ({
                          ...d,
                          notes: e.target.value
                        }))} placeholder="Notes (optional)" style={{
                          ...inputStyle,
                          marginTop: 8,
                          fontSize: 12
                        }} />
                              <div style={{
                          display: "flex",
                          gap: 8,
                          marginTop: 8
                        }}>
                                <button onClick={() => saveRoomEdit(r.id)} style={{
                            background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
                            color: "#FFFFFF",
                            border: "none",
                            padding: "7px 16px",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer"
                          }}><Save className="inline w-[1em] h-[1em] align-[-0.125em]" /> Save</button>
                                <button onClick={cancelEditRoom} style={{
                            background: "#FFFFFF",
                            color: "#607D8B",
                            border: `1px solid ${LIGHT}`,
                            padding: "7px 14px",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer"
                          }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /> Cancel</button>
                              </div>
                            </> : <>
                              <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 10,
                          flexWrap: "wrap"
                        }}>
                                <div style={{
                            fontWeight: 700,
                            color: NAVY,
                            fontSize: 13.5
                          }}>Room {r.roomNo} <span style={{
                              fontWeight: 500,
                              color: "#607D8B",
                              fontSize: 12
                            }}>· Cap {r.capacity}</span></div>
                                <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8
                          }}>
                                  <span style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: "3px 10px",
                              borderRadius: 20,
                              background: full ? "#FFEBEE" : "#E8F5E9",
                              color: full ? "#C62828" : "#2E7D32"
                            }}>{occ}/{r.capacity} {full ? "FULL" : ""}</span>
                                  <button onClick={() => startEditRoom(r)} style={{
                              background: "#FFFFFF",
                              color: NAVY,
                              border: `1px solid ${LIGHT}`,
                              padding: "5px 10px",
                              borderRadius: 8,
                              fontSize: 11.5,
                              fontWeight: 700,
                              cursor: "pointer"
                            }}><Pencil className="inline w-[1em] h-[1em] align-[-0.125em]" /> Edit</button>
                                  <button onClick={() => deleteRoom(r.id)} style={{
                              background: "none",
                              border: "none",
                              color: "#C62828",
                              cursor: "pointer",
                              fontSize: 13
                            }} title="Delete room"><Trash2 className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>
                                </div>
                              </div>
                              {r.notes && <div style={{
                          fontSize: 12,
                          color: "#607D8B",
                          marginTop: 6
                        }}>{r.notes}</div>}
                              <div style={{
                          fontSize: 12,
                          color: "#607D8B",
                          marginTop: 6
                        }}>
                                <b>Occupants:</b> {occupants.length === 0 ? "— empty —" : occupants.map(o => o.name).join(", ")}
                              </div>
                            </>}
                        </div>;
                  })}
                  </div>
                  <div style={{
                  background: "#FFFFFF",
                  borderRadius: 16,
                  border: `1px solid ${LIGHT}`,
                  padding: 16
                }}>
                    <div style={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: NAVY,
                    marginBottom: 10
                  }}><Plus className="inline w-[1em] h-[1em] align-[-0.125em]" /> Add Room to Floor {activeFloor.floorNo}</div>
                    <div style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    gap: 10,
                    marginBottom: 10
                  }}>
                      <input placeholder="Room No" value={roomForm.roomNo} onChange={e => setRoomForm(f => ({
                      ...f,
                      roomNo: e.target.value
                    }))} style={inputStyle} />
                      <input type="number" min="1" placeholder="Capacity" value={roomForm.capacity} onChange={e => setRoomForm(f => ({
                      ...f,
                      capacity: e.target.value
                    }))} style={inputStyle} />
                    </div>
                    <input placeholder="Notes (optional)" value={roomForm.notes} onChange={e => setRoomForm(f => ({
                    ...f,
                    notes: e.target.value
                  }))} style={{
                    ...inputStyle,
                    marginBottom: 10
                  }} />
                    {roomError && <div style={{
                    background: "#FFEBEE",
                    color: "#C62828",
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontSize: 12.5,
                    marginBottom: 10
                  }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {roomError}</div>}
                    <button onClick={addRoom} style={{
                    background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
                    color: "#FFFFFF",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer"
                  }}>Add Room</button>
                  </div>
                </div>

                {/* Washrooms on this floor */}
                <div>
                  <div style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: NAVY,
                  marginBottom: 10
                }}><ShowerHead className="inline w-[1em] h-[1em] align-[-0.125em]" /> Washrooms on Floor {activeFloor.floorNo}</div>
                  <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  marginBottom: 16
                }}>
                    {activeFloor.washrooms.length === 0 ? <EmptyBox text="No washrooms added yet." /> : activeFloor.washrooms.map(w => {
                    const isEditing = editingWashroomId === w.id;
                    return <div key={w.id} style={{
                      background: "#FFFFFF",
                      borderRadius: 14,
                      border: `1px solid ${isEditing ? BLUE : LIGHT}`,
                      padding: "12px 16px"
                    }}>
                          {isEditing ? <>
                              <input value={washroomDraft.label} onChange={e => setWashroomDraft(d => ({
                          ...d,
                          label: e.target.value
                        }))} style={{
                          ...inputStyle,
                          fontWeight: 700,
                          color: NAVY,
                          padding: "6px 10px",
                          marginBottom: 8
                        }} autoFocus />
                              <div style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                          gap: 8,
                          marginBottom: 8
                        }}>
                                <div>
                                  <div style={{
                              fontSize: 10.5,
                              color: "#607D8B",
                              marginBottom: 4
                            }}><ShowerHead className="inline w-[1em] h-[1em] align-[-0.125em]" /> Showers</div>
                                  <input type="number" min="0" value={washroomDraft.showers} onChange={e => setWashroomDraft(d => ({
                              ...d,
                              showers: e.target.value
                            }))} style={{
                              ...inputStyle,
                              padding: "6px 8px",
                              textAlign: "center"
                            }} />
                                </div>
                                <div>
                                  <div style={{
                              fontSize: 10.5,
                              color: "#607D8B",
                              marginBottom: 4
                            }}><Lightbulb className="inline w-[1em] h-[1em] align-[-0.125em]" /> Lights</div>
                                  <input type="number" min="0" value={washroomDraft.lights} onChange={e => setWashroomDraft(d => ({
                              ...d,
                              lights: e.target.value
                            }))} style={{
                              ...inputStyle,
                              padding: "6px 8px",
                              textAlign: "center"
                            }} />
                                </div>
                                <div>
                                  <div style={{
                              fontSize: 10.5,
                              color: "#607D8B",
                              marginBottom: 4
                            }}><Droplet className="inline w-[1em] h-[1em] align-[-0.125em]" /> Wash Basins</div>
                                  <input type="number" min="0" value={washroomDraft.washbasins} onChange={e => setWashroomDraft(d => ({
                              ...d,
                              washbasins: e.target.value
                            }))} style={{
                              ...inputStyle,
                              padding: "6px 8px",
                              textAlign: "center"
                            }} />
                                </div>
                              </div>
                              <div style={{
                          display: "flex",
                          gap: 8
                        }}>
                                <button onClick={() => saveWashroomEdit(w.id)} style={{
                            background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
                            color: "#FFFFFF",
                            border: "none",
                            padding: "7px 16px",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer"
                          }}><Save className="inline w-[1em] h-[1em] align-[-0.125em]" /> Save</button>
                                <button onClick={cancelEditWashroom} style={{
                            background: "#FFFFFF",
                            color: "#607D8B",
                            border: `1px solid ${LIGHT}`,
                            padding: "7px 14px",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer"
                          }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /> Cancel</button>
                              </div>
                            </> : <>
                              <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 8
                        }}>
                                <div style={{
                            fontWeight: 700,
                            color: NAVY,
                            fontSize: 13.5
                          }}>{w.label}</div>
                                <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8
                          }}>
                                  <button onClick={() => startEditWashroom(w)} style={{
                              background: "#FFFFFF",
                              color: NAVY,
                              border: `1px solid ${LIGHT}`,
                              padding: "5px 10px",
                              borderRadius: 8,
                              fontSize: 11.5,
                              fontWeight: 700,
                              cursor: "pointer"
                            }}><Pencil className="inline w-[1em] h-[1em] align-[-0.125em]" /> Edit</button>
                                  <button onClick={() => deleteWashroom(w.id)} style={{
                              background: "none",
                              border: "none",
                              color: "#C62828",
                              cursor: "pointer",
                              fontSize: 13
                            }} title="Delete washroom"><Trash2 className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>
                                </div>
                              </div>
                              <div style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                          gap: 8,
                          fontSize: 12.5,
                          color: "#37474F"
                        }}>
                                <div><ShowerHead className="inline w-[1em] h-[1em] align-[-0.125em]" /> Showers: <b>{w.showers}</b></div>
                                <div><Lightbulb className="inline w-[1em] h-[1em] align-[-0.125em]" /> Lights: <b>{w.lights}</b></div>
                                <div><Droplet className="inline w-[1em] h-[1em] align-[-0.125em]" /> Basins: <b>{w.washbasins}</b></div>
                              </div>
                            </>}
                        </div>;
                  })}
                  </div>
                  <div style={{
                  background: "#FFFFFF",
                  borderRadius: 16,
                  border: `1px solid ${LIGHT}`,
                  padding: 16
                }}>
                    <div style={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: NAVY,
                    marginBottom: 10
                  }}><Plus className="inline w-[1em] h-[1em] align-[-0.125em]" /> Add Washroom</div>
                    <input placeholder="Label e.g. Washroom 6" value={washroomForm.label} onChange={e => setWashroomForm(f => ({
                    ...f,
                    label: e.target.value
                  }))} style={{
                    ...inputStyle,
                    marginBottom: 10
                  }} />
                    <div style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                    gap: 8,
                    marginBottom: 10
                  }}>
                      <input type="number" min="0" placeholder="Showers" value={washroomForm.showers} onChange={e => setWashroomForm(f => ({
                      ...f,
                      showers: e.target.value
                    }))} style={inputStyle} />
                      <input type="number" min="0" placeholder="Lights" value={washroomForm.lights} onChange={e => setWashroomForm(f => ({
                      ...f,
                      lights: e.target.value
                    }))} style={inputStyle} />
                      <input type="number" min="0" placeholder="Basins" value={washroomForm.washbasins} onChange={e => setWashroomForm(f => ({
                      ...f,
                      washbasins: e.target.value
                    }))} style={inputStyle} />
                    </div>
                    {washroomError && <div style={{
                    background: "#FFEBEE",
                    color: "#C62828",
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontSize: 12.5,
                    marginBottom: 10
                  }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {washroomError}</div>}
                    <button onClick={addWashroom} style={{
                    background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
                    color: "#FFFFFF",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer"
                  }}>Add Washroom</button>
                  </div>

                  {/* Common rules */}
                  <div style={{
                  background: "#FFFFFF",
                  borderRadius: 16,
                  border: `1px solid ${LIGHT}`,
                  padding: 16,
                  marginTop: 16
                }}>
                    <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 4
                  }}>
                      <div style={{
                      fontWeight: 700,
                      fontSize: 13,
                      color: NAVY
                    }}><Pin className="inline w-[1em] h-[1em] align-[-0.125em]" /> Common Hostel Rules</div>
                      <button onClick={printRules} style={{
                      background: PALE,
                      color: NAVY,
                      border: `1px solid ${LIGHT}`,
                      padding: "5px 12px",
                      borderRadius: 8,
                      fontSize: 11.5,
                      fontWeight: 700,
                      cursor: "pointer"
                    }}><Printer className="inline w-[1em] h-[1em] align-[-0.125em]" /> Print</button>
                    </div>
                    <div style={{
                    fontSize: 11,
                    color: "#607D8B",
                    marginBottom: 10
                  }}>Applies to the whole {HOSTEL_NAME}</div>
                    <ul style={{
                    margin: 0,
                    padding: 0,
                    listStyle: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    marginBottom: 12
                  }}>
                      {rules.map((rule, i) => <li key={i} style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      fontSize: 12,
                      color: "#37474F",
                      background: PALE,
                      borderRadius: 10,
                      padding: "7px 10px"
                    }}>
                          <span style={{
                        flex: 1
                      }}>• {rule}</span>
                          <button onClick={() => removeRule(i)} style={{
                        background: "none",
                        border: "none",
                        color: "#C62828",
                        cursor: "pointer",
                        fontSize: 12,
                        flexShrink: 0
                      }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>
                        </li>)}
                    </ul>
                    <div style={{
                    display: "flex",
                    gap: 8
                  }}>
                      <input value={newRule} onChange={e => setNewRule(e.target.value)} placeholder="Add a rule..." style={{
                      ...inputStyle,
                      fontSize: 12
                    }} />
                      <button onClick={addRule} style={{
                      background: NAVY,
                      color: "#FFFFFF",
                      border: "none",
                      padding: "0 14px",
                      borderRadius: 10,
                      fontSize: 12.5,
                      fontWeight: 700,
                      cursor: "pointer"
                    }}>Add</button>
                    </div>
                  </div>
                </div>
              </div>
            </>}
        </div>}

      {/* ══════════════ REGISTER STUDENT TAB ══════════════ */}
      {activeTab === "register" && <>
        <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 340px",
            gap: 20,
            alignItems: "start"
          }}>
          <div style={{
              background: "#FFFFFF",
              borderRadius: 20,
              border: `1px solid ${LIGHT}`,
              padding: 24,
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
            }}>
            <div style={{
                fontWeight: 800,
                fontSize: 16,
                color: NAVY,
                marginBottom: 4
              }}>Register a New {HOSTEL_NAME} Student</div>
            <div style={{
                fontSize: 12,
                color: "#607D8B",
                marginBottom: 20
              }}>Fill in the student's details. The Block &amp; Floor auto-fill from Class (editable below), then allot a room.</div>

            <div style={{
                marginBottom: 16,
                position: "relative"
              }} ref={nameFieldRef}>
              <label style={labelStyle}>STUDENT NAME</label>
              <input value={regForm.name} onChange={e => handleRegNameChange(e.target.value)} onFocus={() => {
                  if (nameSuggestions.length) setShowNameSuggestions(true);
                }} placeholder="Full name" style={inputStyle} autoComplete="off" />
              {showNameSuggestions && nameSuggestions.length > 0 && <div style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  marginTop: 4,
                  zIndex: 20,
                  background: "#FFFFFF",
                  border: `1px solid ${LIGHT}`,
                  borderRadius: 12,
                  boxShadow: "0 6px 20px rgba(13,71,161,0.15)",
                  maxHeight: 240,
                  overflowY: "auto"
                }}>
                  {nameSuggestions.map(adm => <div key={adm._id} onClick={() => selectNameSuggestion(adm)} style={{
                    padding: "9px 14px",
                    cursor: "pointer",
                    borderBottom: `1px solid ${PALE}`,
                    fontSize: 12.5
                  }} onMouseDown={e => e.preventDefault()}>
                      <div style={{
                      fontWeight: 700,
                      color: NAVY
                    }}>{adm.studentName}</div>
                      <div style={{
                      color: "#607D8B",
                      fontSize: 11,
                      marginTop: 2
                    }}>
                        Class {adm.applyingClass}{adm.section ? `-${adm.section}` : ""} · Father: {adm.fatherName || "—"}
                      </div>
                    </div>)}
                </div>}
            </div>

            <div style={{
                marginBottom: 16
              }}>
              <label style={labelStyle}>ADDRESS</label>
              <textarea value={regForm.address} onChange={e => setRegForm(f => ({
                  ...f,
                  address: e.target.value
                }))} placeholder="Home address" rows={2} style={{
                  ...inputStyle,
                  resize: "vertical",
                  fontFamily: "inherit"
                }} />
            </div>

            <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 14,
                marginBottom: 16
              }}>
              <div>
                <label style={labelStyle}>PARENT NUMBER</label>
                <input value={regForm.parentNumber} onChange={e => setRegForm(f => ({
                    ...f,
                    parentNumber: e.target.value
                  }))} placeholder="10-digit mobile number" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>ALTERNATIVE NUMBER</label>
                <input value={regForm.altNumber} onChange={e => setRegForm(f => ({
                    ...f,
                    altNumber: e.target.value
                  }))} placeholder="Optional" style={inputStyle} />
              </div>
            </div>

            <div style={{
                marginBottom: 16
              }}>
              <label style={labelStyle}>SCHOOL NAME</label>
              <input value={regForm.schoolName} onChange={e => setRegForm(f => ({
                  ...f,
                  schoolName: e.target.value
                }))} placeholder="School / institution name" style={inputStyle} />
            </div>

            <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 14,
                marginBottom: 16
              }}>
              <div>
                <label style={labelStyle}>CLASS</label>
                <input value={regForm.className} onChange={e => handleClassChange(e.target.value)} placeholder="e.g. 9" style={inputStyle} />
                {regForm.className && <div style={{
                    fontSize: 11,
                    marginTop: 5,
                    color: regAutoMatched ? "#2E7D32" : "#E65100"
                  }}>
                    {regAutoMatched ? "Block & Floor auto-matched from class." : " No floor configured for this class — select Block & Floor manually."}
                  </div>}
              </div>
              <div>
                <label style={labelStyle}>SECTION</label>
                <input value={regForm.section} onChange={e => setRegForm(f => ({
                    ...f,
                    section: e.target.value
                  }))} placeholder="e.g. A" style={inputStyle} />
              </div>
            </div>

            <div style={{
                marginBottom: 16
              }}>
              <label style={labelStyle}>ACADEMIC YEAR / BATCH</label>
              <select value={regForm.academicYear} onChange={e => setRegForm(f => ({
                  ...f,
                  academicYear: e.target.value
                }))} style={{
                  ...inputStyle,
                  cursor: "pointer"
                }}>
                {academicYearOptions().map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 14,
                marginBottom: 16
              }}>
              <div>
                <label style={labelStyle}>BLOCK</label>
                <select value={regForm.blockId} onChange={e => setRegForm(f => ({
                    ...f,
                    blockId: e.target.value,
                    floorId: "",
                    roomId: ""
                  }))} style={{
                    ...inputStyle,
                    cursor: "pointer"
                  }}>
                  <option value="">— Select block —</option>
                  {regBlockOptions().map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>FLOOR</label>
                <select value={regForm.floorId} onChange={e => setRegForm(f => ({
                    ...f,
                    floorId: e.target.value,
                    roomId: ""
                  }))} style={{
                    ...inputStyle,
                    cursor: "pointer"
                  }} disabled={!regForm.blockId}>
                  <option value="">— Select floor —</option>
                  {regFloorOptions().map(f => <option key={f.id} value={f.id}>Floor {f.floorNo}{f.classes.length ? ` (Class ${f.classes.join(", ")})` : ""}</option>)}
                </select>
              </div>
            </div>

            <div style={{
                marginBottom: 18
              }}>
              <label style={labelStyle}>ALLOT ROOM</label>
              <select value={regForm.roomId} onChange={e => setRegForm(f => ({
                  ...f,
                  roomId: e.target.value
                }))} style={{
                  ...inputStyle,
                  cursor: "pointer"
                }} disabled={!regForm.floorId}>
                <option value="">— Select a room —</option>
                {regRoomOptions().map(r => {
                    const occ = occupancyByRoom[r.id] || 0;
                    const full = occ >= r.capacity;
                    return <option key={r.id} value={r.id} disabled={full}>Room {r.roomNo} — {occ}/{r.capacity} occupied{full ? " (FULL)" : ""}</option>;
                  })}
              </select>
              {regForm.floorId && regRoomOptions().length === 0 && <div style={{
                  fontSize: 12,
                  color: "#E65100",
                  marginTop: 6
                }}>No rooms on this floor yet — add one in the "Rooms &amp; Washrooms" tab first.</div>}
            </div>

            {regError && <div style={{
                background: "#FFEBEE",
                color: "#C62828",
                padding: "10px 14px",
                borderRadius: 10,
                fontSize: 13,
                marginBottom: 14
              }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {regError}</div>}
            {regSuccess && <div style={{
                background: "#E8F5E9",
                color: "#2E7D32",
                padding: "10px 14px",
                borderRadius: 10,
                fontSize: 13,
                marginBottom: 14
              }}>{regSuccess}</div>}

            <div style={{
                display: "flex",
                gap: 10
              }}>
              <button onClick={submitRegistration} style={{
                  flex: 1,
                  background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
                  color: "#FFFFFF",
                  border: "none",
                  padding: "13px 0",
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer"
                }}>Register &amp; Allot Room</button>
              <button onClick={() => {
                  setRegForm(emptyRegForm);
                  setRegError("");
                  setRegSuccess("");
                  setRegAutoMatched(false);
                }} style={{
                  background: "#FFFFFF",
                  color: NAVY,
                  border: `1px solid ${LIGHT}`,
                  padding: "13px 20px",
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer"
                }}>Clear</button>
            </div>
          </div>

          {/* Quick room availability sidebar */}
          <div style={{
              background: "#FFFFFF",
              borderRadius: 20,
              border: `1px solid ${LIGHT}`,
              padding: 18,
              position: isMobile ? "static" : "sticky",
              top: 24,
              maxHeight: isMobile ? "none" : "calc(100vh - 48px)",
              display: "flex",
              flexDirection: "column"
            }}>
            <div style={{
                fontWeight: 700,
                fontSize: 14,
                color: NAVY,
                marginBottom: 12,
                flexShrink: 0
              }}><BedDouble className="inline w-[1em] h-[1em] align-[-0.125em]" /> Room Availability</div>
            <div style={{
                overflowY: "auto",
                paddingRight: 4
              }}>
            {blocks.map(b => <div key={b.id} style={{
                  marginBottom: 14
                }}>
                <div style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: NAVY,
                    marginBottom: 6
                  }}>{b.name}</div>
                {b.floors.map(f => <div key={f.id} style={{
                    marginBottom: 8
                  }}>
                    <div style={{
                      fontSize: 11,
                      color: "#607D8B",
                      marginBottom: 4
                    }}>Floor {f.floorNo} (Class {f.classes.join(", ") || "—"})</div>
                    <div style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 5
                    }}>
                      {f.rooms.map(r => {
                        const occ = occupancyByRoom[r.id] || 0;
                        const full = occ >= r.capacity;
                        return <div key={r.id} style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "6px 10px",
                          borderRadius: 8,
                          background: full ? "#FFEBEE" : PALE,
                          fontSize: 12
                        }}>
                            <span style={{
                            color: "#1A1A1A",
                            fontWeight: 600
                          }}>Room {r.roomNo}</span>
                            <span style={{
                            color: full ? "#C62828" : "#2E7D32",
                            fontWeight: 700
                          }}>{occ}/{r.capacity}</span>
                          </div>;
                      })}
                    </div>
                  </div>)}
              </div>)}
            </div>
          </div>
        </div>

        {/* ── Registered Students table (persisted, filterable by batch) ── */}
        <div style={{
            background: "#FFFFFF",
            borderRadius: 20,
            border: `1px solid ${LIGHT}`,
            padding: 24,
            marginTop: 20,
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
          }}>
          <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 16
            }}>
            <div>
              <div style={{
                  fontWeight: 800,
                  fontSize: 15,
                  color: NAVY
                }}><ClipboardList className="inline w-[1em] h-[1em] align-[-0.125em]" /> Registered Students</div>
              <div style={{
                  fontSize: 12,
                  color: "#607D8B",
                  marginTop: 2
                }}>Every student registered &amp; allotted a room in the {HOSTEL_NAME}, saved to the database.</div>
            </div>
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8
              }}>
              <label style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: NAVY
                }}>BATCH / YEAR</label>
              <select value={regYearFilter} onChange={e => setRegYearFilter(e.target.value)} style={{
                  ...inputStyle,
                  width: "auto",
                  cursor: "pointer"
                }}>
                <option value="All">All Years</option>
                {regYearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {registeredStudentsTable.length === 0 ? <EmptyBox text={regYearFilter === "All" ? "No students registered yet." : `No students registered for ${regYearFilter}.`} /> : <div style={{
              overflow: "auto",
              borderRadius: 14,
              border: `1px solid ${PALE}`
            }}>
              <table style={{
                borderCollapse: "collapse",
                width: "100%",
                minWidth: 980
              }}>
                <thead>
                  <tr style={{
                    background: PALE
                  }}>
                    <th style={thStyle}>STUDENT</th>
                    <th style={thStyle}>CLASS / SECTION</th>
                    <th style={thStyle}>SCHOOL</th>
                    <th style={thStyle}>PARENT CONTACT</th>
                    <th style={thStyle}>ADDRESS</th>
                    <th style={thStyle}>ROOM ALLOTTED</th>
                    <th style={thStyle}>BATCH / YEAR</th>
                    <th style={thStyle}>REGISTERED ON</th>
                    <th style={thStyle}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {registeredStudentsTable.map((s, i) => {
                    const isEditing = editingStudentId === s.id;
                    return <Fragment key={s.id}>
                        <tr style={{
                        borderTop: i > 0 ? `1px solid ${PALE}` : "none",
                        background: isEditing ? PALE : "transparent"
                      }}>
                          <td style={{
                          ...tdStyle,
                          fontWeight: 700,
                          color: "#1A1A1A"
                        }}>{s.name}</td>
                          <td style={tdStyle}>{s.className}{s.section ? ` - ${s.section}` : ""}</td>
                          <td style={tdStyle}>{s.schoolName || "—"}</td>
                          <td style={tdStyle}>
                            <div>{s.parentNumber}</div>
                            {s.altNumber && <div style={{
                            fontSize: 11,
                            color: "#607D8B"
                          }}>{s.altNumber}</div>}
                          </td>
                          <td style={{
                          ...tdStyle,
                          maxWidth: 220,
                          whiteSpace: "normal"
                        }}>{s.address}</td>
                          <td style={tdStyle}>{roomPathFor(s.roomId)}</td>
                          <td style={tdStyle}>
                            <span style={{
                            background: "#FFFFFF",
                            color: NAVY,
                            padding: "3px 10px",
                            borderRadius: 20,
                            fontSize: 11.5,
                            fontWeight: 700,
                            border: `1px solid ${LIGHT}`
                          }}>{s.academicYear || "—"}</span>
                          </td>
                          <td style={{
                          ...tdStyle,
                          fontSize: 12,
                          color: "#607D8B"
                        }}>{fmtDate(s.createdAt)}</td>
                          <td style={tdStyle}>
                            {isEditing ? <button onClick={cancelEditStudent} style={{
                            background: "#FFFFFF",
                            color: "#607D8B",
                            border: `1px solid ${LIGHT}`,
                            padding: "6px 12px",
                            borderRadius: 8,
                            fontSize: 11.5,
                            fontWeight: 700,
                            cursor: "pointer"
                          }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /> Cancel</button> : <button onClick={() => startEditStudent(s)} style={{
                            background: "#FFFFFF",
                            color: NAVY,
                            border: `1px solid ${LIGHT}`,
                            padding: "6px 12px",
                            borderRadius: 8,
                            fontSize: 11.5,
                            fontWeight: 700,
                            cursor: "pointer"
                          }}><Pencil className="inline w-[1em] h-[1em] align-[-0.125em]" /> Edit</button>}
                          </td>
                        </tr>

                        {isEditing && <tr style={{}}>
                            <td colSpan={9} style={{
                          padding: "16px 18px",
                          background: PALE,
                          borderTop: `1px solid ${LIGHT}`
                        }}>
                              <div style={{
                            fontWeight: 800,
                            fontSize: 13,
                            color: NAVY,
                            marginBottom: 12
                          }}><Pencil className="inline w-[1em] h-[1em] align-[-0.125em]" /> Editing {s.name}</div>
                              <div style={{
                            display: "grid",
                            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                            gap: 12,
                            marginBottom: 12
                          }}>
                                <div>
                                  <label style={labelStyle}>STUDENT NAME</label>
                                  <input value={studentEditForm.name} onChange={e => setStudentEditForm(f => ({
                                ...f,
                                name: e.target.value
                              }))} style={inputStyle} />
                                </div>
                                <div>
                                  <label style={labelStyle}>PARENT NUMBER</label>
                                  <input value={studentEditForm.parentNumber} onChange={e => setStudentEditForm(f => ({
                                ...f,
                                parentNumber: e.target.value
                              }))} style={inputStyle} />
                                </div>
                                <div>
                                  <label style={labelStyle}>ALTERNATIVE NUMBER</label>
                                  <input value={studentEditForm.altNumber} onChange={e => setStudentEditForm(f => ({
                                ...f,
                                altNumber: e.target.value
                              }))} style={inputStyle} />
                                </div>
                              </div>

                              <div style={{
                            marginBottom: 12
                          }}>
                                <label style={labelStyle}>ADDRESS</label>
                                <textarea value={studentEditForm.address} onChange={e => setStudentEditForm(f => ({
                              ...f,
                              address: e.target.value
                            }))} rows={2} style={{
                              ...inputStyle,
                              resize: "vertical",
                              fontFamily: "inherit"
                            }} />
                              </div>

                              <div style={{
                            display: "grid",
                            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr",
                            gap: 12,
                            marginBottom: 12
                          }}>
                                <div>
                                  <label style={labelStyle}>SCHOOL NAME</label>
                                  <input value={studentEditForm.schoolName} onChange={e => setStudentEditForm(f => ({
                                ...f,
                                schoolName: e.target.value
                              }))} style={inputStyle} />
                                </div>
                                <div>
                                  <label style={labelStyle}>CLASS</label>
                                  <input value={studentEditForm.className} onChange={e => setStudentEditForm(f => ({
                                ...f,
                                className: e.target.value
                              }))} style={inputStyle} />
                                </div>
                                <div>
                                  <label style={labelStyle}>SECTION</label>
                                  <input value={studentEditForm.section} onChange={e => setStudentEditForm(f => ({
                                ...f,
                                section: e.target.value
                              }))} style={inputStyle} />
                                </div>
                                <div>
                                  <label style={labelStyle}>ACADEMIC YEAR / BATCH</label>
                                  <select value={studentEditForm.academicYear} onChange={e => setStudentEditForm(f => ({
                                ...f,
                                academicYear: e.target.value
                              }))} style={{
                                ...inputStyle,
                                cursor: "pointer"
                              }}>
                                    {regYearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                                  </select>
                                </div>
                              </div>

                              <div style={{
                            display: "grid",
                            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                            gap: 12,
                            marginBottom: 14
                          }}>
                                <div>
                                  <label style={labelStyle}>BLOCK</label>
                                  <select value={studentEditForm.blockId} onChange={e => setStudentEditForm(f => ({
                                ...f,
                                blockId: e.target.value,
                                floorId: "",
                                roomId: ""
                              }))} style={{
                                ...inputStyle,
                                cursor: "pointer"
                              }}>
                                    <option value="">— Select block —</option>
                                    {blocks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label style={labelStyle}>FLOOR</label>
                                  <select value={studentEditForm.floorId} onChange={e => setStudentEditForm(f => ({
                                ...f,
                                floorId: e.target.value,
                                roomId: ""
                              }))} style={{
                                ...inputStyle,
                                cursor: "pointer"
                              }} disabled={!studentEditForm.blockId}>
                                    <option value="">— Select floor —</option>
                                    {studentEditFloorOptions().map(f => <option key={f.id} value={f.id}>Floor {f.floorNo}{f.classes.length ? ` (Class ${f.classes.join(", ")})` : ""}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label style={labelStyle}>ALLOT ROOM</label>
                                  <select value={studentEditForm.roomId} onChange={e => setStudentEditForm(f => ({
                                ...f,
                                roomId: e.target.value
                              }))} style={{
                                ...inputStyle,
                                cursor: "pointer"
                              }} disabled={!studentEditForm.floorId}>
                                    <option value="">— Select a room —</option>
                                    {studentEditRoomOptions().map(r => {
                                  const occ = students.filter(st => st.roomId === r.id && st.id !== s.id).length;
                                  const full = occ >= r.capacity;
                                  return <option key={r.id} value={r.id} disabled={full}>Room {r.roomNo} — {occ}/{r.capacity} occupied{full ? " (FULL)" : ""}</option>;
                                })}
                                  </select>
                                </div>
                              </div>

                              {studentEditError && <div style={{
                            background: "#FFEBEE",
                            color: "#C62828",
                            padding: "10px 14px",
                            borderRadius: 10,
                            fontSize: 13,
                            marginBottom: 12
                          }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {studentEditError}</div>}

                              <div style={{
                            display: "flex",
                            gap: 10
                          }}>
                                <button onClick={() => saveStudentEdit(s.id)} style={{
                              background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
                              color: "#FFFFFF",
                              border: "none",
                              padding: "10px 22px",
                              borderRadius: 10,
                              fontSize: 13,
                              fontWeight: 700,
                              cursor: "pointer"
                            }}><Save className="inline w-[1em] h-[1em] align-[-0.125em]" /> Save Changes</button>
                                <button onClick={cancelEditStudent} style={{
                              background: "#FFFFFF",
                              color: NAVY,
                              border: `1px solid ${LIGHT}`,
                              padding: "10px 22px",
                              borderRadius: 10,
                              fontSize: 13,
                              fontWeight: 700,
                              cursor: "pointer"
                            }}>Cancel</button>
                              </div>
                            </td>
                          </tr>}
                      </Fragment>;
                  })}
                </tbody>
              </table>
            </div>}
        </div>
        </>}


      {/* ══════════════ COMPLAINTS TAB ══════════════ */}
      {activeTab === "complaints" && <div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
            marginBottom: 20
          }}>
            <StatCard label="Total Complaints" value={complaintStats.total} color={NAVY} icon="" />
            <StatCard label="Pending" value={complaintStats.pending} color="#E65100" icon="⏳" />
            <StatCard label="In Progress" value={complaintStats.inProgress} color={BLUE} icon="" />
            <StatCard label="Resolved" value={complaintStats.resolved} color="#2E7D32" icon="" />
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
            gap: 20,
            alignItems: "start"
          }}>
            <div>
              <div style={{
                display: "flex",
                gap: 8,
                marginBottom: 16,
                flexWrap: "wrap"
              }}>
                {["All", "Pending", "In Progress", "Resolved"].map(s => <button key={s} onClick={() => setComplaintFilter(s)} style={{
                  border: `1px solid ${complaintFilter === s ? NAVY : LIGHT}`,
                  background: complaintFilter === s ? PALE : "#FFFFFF",
                  color: NAVY,
                  padding: "6px 16px",
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer"
                }}>{s} {s !== "All" ? `(${complaints.filter(c => c.status === s).length})` : `(${complaints.length})`}</button>)}
              </div>

              {filteredComplaints.length === 0 ? <EmptyBox text={`No ${complaintFilter !== "All" ? complaintFilter.toLowerCase() + " " : ""}complaints.`} /> : <div style={{
                display: "flex",
                flexDirection: "column",
                gap: 12
              }}>
                  {filteredComplaints.map(c => {
                  const student = studentById(c.studentId);
                  const meta = findRoomMeta(blocks, student?.roomId);
                  const sc = COMPLAINT_STATUS_COLOR[c.status] || COMPLAINT_STATUS_COLOR.Pending;
                  return <div key={c.id} style={{
                    background: "#FFFFFF",
                    borderRadius: 16,
                    border: `1px solid ${LIGHT}`,
                    padding: "16px 18px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
                  }}>
                        <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      gap: 10
                    }}>
                          <div>
                            <div style={{
                          fontWeight: 700,
                          fontSize: 15,
                          color: "#1A1A1A"
                        }}>
                              {CATEGORY_ICON[c.category] || <Wrench className="inline w-[1em] h-[1em] align-[-0.125em]" />} {c.category}
                              <span style={{
                            fontWeight: 500,
                            fontSize: 12,
                            color: "#607D8B"
                          }}>
                                {" "}· {student?.name || "Unknown"}{meta ? ` · ${meta.blockName} · Room ${meta.roomNo}` : ""}
                              </span>
                            </div>
                            <span style={{
                          background: sc.bg,
                          color: sc.text,
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "3px 10px",
                          borderRadius: 20,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          marginTop: 6
                        }}>
                              <span style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: sc.dot
                          }} /> {c.status}
                            </span>
                          </div>
                          <div style={{
                        display: "flex",
                        gap: 8
                      }}>
                            {c.status !== "In Progress" && <button onClick={() => updateComplaintStatus(c.id, "In Progress")} style={{
                          background: BLUE,
                          color: "#FFFFFF",
                          border: "none",
                          padding: "7px 14px",
                          borderRadius: 10,
                          fontSize: 11.5,
                          fontWeight: 700,
                          cursor: "pointer"
                        }}>Mark In Progress</button>}
                            {c.status !== "Resolved" && <button onClick={() => updateComplaintStatus(c.id, "Resolved")} style={{
                          background: "#2E7D32",
                          color: "#FFFFFF",
                          border: "none",
                          padding: "7px 14px",
                          borderRadius: 10,
                          fontSize: 11.5,
                          fontWeight: 700,
                          cursor: "pointer"
                        }}>Mark Resolved</button>}
                          </div>
                        </div>

                        <div style={{
                      marginTop: 10,
                      fontSize: 13,
                      color: "#1A1A1A",
                      background: PALE,
                      borderRadius: 10,
                      padding: "8px 12px"
                    }}>{c.description}</div>

                        <div style={{
                      marginTop: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap"
                    }}>
                          <span style={{
                        fontSize: 12,
                        color: "#607D8B"
                      }}><HardHat className="inline w-[1em] h-[1em] align-[-0.125em]" /> Assigned: <b style={{
                          color: "#37474F"
                        }}>{c.assignedTo || "Not assigned"}</b></span>
                          <select value={assignInputs[c.id] || ""} onChange={e => setAssignInputs(prev => ({
                        ...prev,
                        [c.id]: e.target.value
                      }))} style={{
                        ...inputStyle,
                        width: 190,
                        padding: "6px 10px",
                        fontSize: 12,
                        cursor: "pointer"
                      }}>
                            <option value="">— Select {c.category} worker —</option>
                            {getMatchingWorkers(c.category).map(w => <option key={w._id} value={w._id}>{w.name} — {w.role}</option>)}
                          </select>
                          <button onClick={() => assignStaffToComplaint(c.id)} disabled={!assignInputs[c.id]} style={{
                        background: NAVY,
                        color: "#FFFFFF",
                        border: "none",
                        padding: "6px 14px",
                        borderRadius: 10,
                        fontSize: 11.5,
                        fontWeight: 700,
                        cursor: assignInputs[c.id] ? "pointer" : "not-allowed",
                        opacity: assignInputs[c.id] ? 1 : 0.5
                      }}>Assign & Notify</button>
                          {getMatchingWorkers(c.category).length === 0 && <span style={{
                        fontSize: 11,
                        color: "#C62828"
                      }}>No workers available</span>}
                        </div>
                      </div>;
                })}
                </div>}
            </div>

            <div style={{
              background: "#FFFFFF",
              borderRadius: 16,
              border: `1px solid ${LIGHT}`,
              padding: 18,
              position: isMobile ? "static" : "sticky",
              top: 24
            }}>
              <div style={{
                fontWeight: 700,
                fontSize: 14,
                color: NAVY,
                marginBottom: 12
              }}><Plus className="inline w-[1em] h-[1em] align-[-0.125em]" /> Raise a Complaint</div>
              <div style={{
                marginBottom: 12
              }}>
                <label style={labelStyle}>STUDENT</label>
                <select value={complaintForm.studentId} onChange={e => setComplaintForm(f => ({
                  ...f,
                  studentId: e.target.value
                }))} style={{
                  ...inputStyle,
                  cursor: "pointer"
                }}>
                  <option value="">— Select student —</option>
                  {students.map(s => {
                    const meta = findRoomMeta(blocks, s.roomId);
                    return <option key={s.id} value={s.id}>{s.name}{meta ? ` (Room ${meta.roomNo})` : ""}</option>;
                  })}
                </select>
              </div>
              <div style={{
                marginBottom: 12
              }}>
                <label style={labelStyle}>CATEGORY</label>
                <select value={complaintForm.category} onChange={e => setComplaintForm(f => ({
                  ...f,
                  category: e.target.value
                }))} style={{
                  ...inputStyle,
                  cursor: "pointer"
                }}>
                  {Object.keys(CATEGORY_ICON).map(cat => <option key={cat} value={cat}>{CATEGORY_ICON[cat]} {cat}</option>)}
                </select>
              </div>
              <div style={{
                marginBottom: 12
              }}>
                <label style={labelStyle}>DESCRIPTION</label>
                <textarea value={complaintForm.description} onChange={e => setComplaintForm(f => ({
                  ...f,
                  description: e.target.value
                }))} placeholder="Describe the issue..." rows={3} style={{
                  ...inputStyle,
                  resize: "vertical",
                  fontFamily: "inherit"
                }} />
              </div>
              {complaintError && <div style={{
                background: "#FFEBEE",
                color: "#C62828",
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: 12.5,
                marginBottom: 10
              }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {complaintError}</div>}
              <button onClick={submitComplaint} style={{
                width: "100%",
                background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
                color: "#FFFFFF",
                border: "none",
                padding: "11px 0",
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer"
              }}>Submit Complaint</button>
            </div>
          </div>
        </div>}

      {/* ══════════════ FEE TAB (read-only Hostel Fee view — T1/T2/T3) ══════════════ */}
      {activeTab === "fee" && <div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
            marginBottom: 20
          }}>
            <StatCard label="Hostel Fee Collected" value={fmtMoney(feeSummaryTotals.paid)} color="#2E7D32" icon="" />
            <StatCard label="Hostel Fee Pending" value={fmtMoney(feeSummaryTotals.pending)} color="#E65100" icon="⏳" />
            <StatCard label="Total Students" value={feeSummaryTotals.count} color={NAVY} icon="" />
          </div>

          {feeLoadError && <div style={{
            background: "#FFEBEE",
            border: "1px solid #FFCDD2",
            color: "#C62828",
            borderRadius: 12,
            padding: "10px 16px",
            marginBottom: 16,
            fontSize: 12.5
          }}>{feeLoadError}</div>}

          {feeLoading ? <div style={{
            padding: 40,
            textAlign: "center",
            color: "#607D8B",
            fontSize: 13
          }}>Loading fee details…</div> : feeSummary.length === 0 ? <EmptyBox text="No hostel students found." /> : <div style={{
            background: "#FFFFFF",
            borderRadius: 16,
            border: `1px solid ${LIGHT}`,
            overflow: "auto",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
          }}>
              <table style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 12.5,
              minWidth: 900
            }}>
                <thead>
                  <tr style={{
                  background: PALE,
                  color: NAVY
                }}>
                    <th style={thStyle}>Student Name</th>
                    <th style={thStyle}>Class</th>
                    <th style={thStyle}>Section</th>
                    <th style={thStyle}>Father Name</th>
                    <th style={{
                    ...thStyle,
                    textAlign: "center"
                  }}>Term 1</th>
                    <th style={{
                    ...thStyle,
                    textAlign: "center"
                  }}>Term 2</th>
                    <th style={{
                    ...thStyle,
                    textAlign: "center"
                  }}>Term 3</th>
                    <th style={{
                    ...thStyle,
                    textAlign: "center"
                  }}>Total Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {feeSummary.map(r => <tr key={r.studentId} style={{
                  borderTop: `1px solid ${LIGHT}`
                }}>
                      <td style={{
                    ...tdStyle,
                    fontWeight: 700,
                    color: "#1A1A1A"
                  }}>{r.name}</td>
                      <td style={tdStyle}>{r.className}</td>
                      <td style={tdStyle}>{r.section}</td>
                      <td style={tdStyle}>{r.fatherName || "—"}</td>
                      <td style={{
                    ...tdStyle,
                    textAlign: "center"
                  }}><FeeTermCell t={r.T1} /></td>
                      <td style={{
                    ...tdStyle,
                    textAlign: "center"
                  }}><FeeTermCell t={r.T2} /></td>
                      <td style={{
                    ...tdStyle,
                    textAlign: "center"
                  }}><FeeTermCell t={r.T3} /></td>
                      <td style={{
                    ...tdStyle,
                    textAlign: "center",
                    fontWeight: 800,
                    color: r.totalPending > 0 ? "#C62828" : "#2E7D32"
                  }}>{fmtMoney(r.totalPending)}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}

      {/* ══════════════ MESS MENU TAB ══════════════ */}
      {activeTab === "mess" && <div>
          {messMenuError && <div style={{
            background: "#FFEBEE",
            border: "1px solid #FFCDD2",
            color: "#C62828",
            borderRadius: 12,
            padding: "10px 16px",
            marginBottom: 16,
            fontSize: 12.5
          }}>{messMenuError}</div>}
          <div style={{
            display: "flex",
            gap: 8,
            marginBottom: 20,
            flexWrap: "wrap"
          }}>
            {DAYS.map(d => <button key={d} onClick={() => setMessDay(d)} style={{
              border: `1px solid ${messDay === d ? NAVY : LIGHT}`,
              background: messDay === d ? NAVY : "#FFFFFF",
              color: messDay === d ? "#FFFFFF" : NAVY,
              padding: "8px 16px",
              borderRadius: 12,
              fontSize: 12.5,
              fontWeight: 700,
              cursor: "pointer"
            }}>{d}</button>)}
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 16,
            marginBottom: 24
          }}>
            {MEALS.map(m => <div key={m.key} style={{
              background: "#FFFFFF",
              borderRadius: 18,
              border: `1px solid ${LIGHT}`,
              padding: 18,
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
            }}>
                <div style={{
                fontWeight: 700,
                fontSize: 14,
                color: NAVY,
                marginBottom: 10
              }}>{m.label}</div>
                <textarea value={messMenu[messDay]?.[m.key] || ""} onChange={e => updateMenu(messDay, m.key, e.target.value)} rows={3} style={{
                ...inputStyle,
                resize: "vertical",
                fontFamily: "inherit"
              }} placeholder={`${m.label} items for ${messDay}...`} />
              </div>)}
          </div>

          <div style={{
            fontWeight: 700,
            fontSize: 14,
            color: NAVY,
            marginBottom: 12
          }}><Calendar className="inline w-[1em] h-[1em] align-[-0.125em]" /> Full Week at a Glance</div>
          <div style={{
            background: "#FFFFFF",
            borderRadius: 18,
            border: `1px solid ${LIGHT}`,
            overflow: "hidden",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
          }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "110px 1fr 1fr 1fr",
              background: PALE,
              padding: "10px 16px",
              fontSize: 11.5,
              fontWeight: 700,
              color: NAVY,
              letterSpacing: "0.4px"
            }}>
              <div>DAY</div><div>BREAKFAST</div><div>LUNCH</div><div>DINNER</div>
            </div>
            {DAYS.map((d, i) => <div key={d} style={{
              display: "grid",
              gridTemplateColumns: "110px 1fr 1fr 1fr",
              padding: "10px 16px",
              fontSize: 12.5,
              color: "#37474F",
              borderTop: i > 0 ? `1px solid ${PALE}` : "none",
              background: d === messDay ? "#F5FAFF" : "#FFFFFF"
            }}>
                <div style={{
                fontWeight: 700,
                color: "#1A1A1A"
              }}>{d}</div>
                <div>{messMenu[d]?.breakfast || "—"}</div>
                <div>{messMenu[d]?.lunch || "—"}</div>
                <div>{messMenu[d]?.dinner || "—"}</div>
              </div>)}
          </div>
        </div>}

      {/* ══════════════ STAFF TAB ══════════════ */}
      {activeTab === "staff" && <div>
          <div style={{
            fontSize: 12.5,
            color: "#607D8B",
            marginBottom: 16
          }}>
            This hostel's Warden{hostelStaff.length > 1 ? " and Sub Warden" : ""} — synced from Staff Management. To add or edit a warden, use "Add Warden" on the Staff Management page.
          </div>
          {staffLoadError && <div style={{
            background: "#FFEBEE",
            color: "#C62828",
            padding: "10px 14px",
            borderRadius: 10,
            fontSize: 12.5,
            marginBottom: 14
          }}>{staffLoadError}</div>}
          {hostelStaff.length === 0 ? <EmptyBox text={`No ${HOSTEL_NAME} Warden has been added yet. Add one from Staff Management "Add Warden".`} /> : <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 16
          }}>
              {hostelStaff.map(s => {
              const isSubWarden = (s.role || "").toLowerCase().endsWith("sub warden");
              const initials = (s.name || "?").trim().split(/\s+/).map(p => p[0]).slice(0, 2).join("").toUpperCase();
              return <div key={s._id} style={{
                background: "#FFFFFF",
                borderRadius: 16,
                border: `1px solid ${LIGHT}`,
                padding: 20,
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
              }}>
                    <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  marginBottom: 14
                }}>
                      <div style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    fontWeight: 800
                  }}>
                        {initials || (isSubWarden ? "" : "")}
                      </div>
                      <div>
                        <div style={{
                      fontWeight: 700,
                      fontSize: 16,
                      color: "#1A1A1A"
                    }}>{s.name}</div>
                        <div style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: NAVY,
                      background: PALE,
                      display: "inline-block",
                      padding: "2px 10px",
                      borderRadius: 20,
                      marginTop: 4
                    }}>
                          {isSubWarden ? "Sub Warden" : "Warden"}
                        </div>
                      </div>
                    </div>
                    <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  fontSize: 13,
                  color: "#37474F"
                }}>
                      <div><Phone className="inline w-[1em] h-[1em] align-[-0.125em]" /> {s.phone || "—"}</div>
                      {s.qual && <div><GraduationCap className="inline w-[1em] h-[1em] align-[-0.125em]" /> {s.qual}</div>}
                      {s.address && <div><Home className="inline w-[1em] h-[1em] align-[-0.125em]" /> {s.address}</div>}
                      {s.salary ? <div><Coins className="inline w-[1em] h-[1em] align-[-0.125em]" /> ₹{Number(s.salary).toLocaleString()} / month</div> : null}
                      <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6
                  }}>
                        <span style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: s.status === "present" || s.status === "active" ? "#43A047" : "#FB8C00"
                    }} />
                        {s.status === "present" || s.status === "active" ? "Present today" : "On leave"}
                      </div>
                    </div>
                  </div>;
            })}
            </div>}
        </div>}

      {/* ══════════════ INVENTORY TAB ══════════════ */}
      {activeTab === "inventory" && <div style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 20,
          alignItems: isMobile ? "stretch" : "flex-start"
        }}>
          {/* ── Main column: search + table ───────────────────────────── */}
          <div style={{
            flex: 1,
            minWidth: 0
          }}>
            <div style={{
              fontSize: 12,
              color: "#607D8B",
              marginBottom: 16
            }}>
              Furniture &amp; fittings count per room across all blocks/floors. Click <b>Edit</b> on a room, change the numbers, then click <b>Save</b> to store them.
            </div>

            {inventoryError && <div style={{
              background: "#FFEBEE",
              border: "1px solid #FFCDD2",
              color: "#C62828",
              borderRadius: 12,
              padding: "10px 16px",
              marginBottom: 16,
              fontSize: 12.5
            }}>{inventoryError}</div>}

            <div style={{
              position: "relative",
              marginBottom: 16
            }}>
              <span style={{
                position: "absolute",
                left: 14,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 14,
                color: "#90A4AE"
              }}><Search className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
              <input type="text" placeholder="Search by room number (e.g. S201)…" value={inventorySearch} onChange={e => setInventorySearch(e.target.value)} style={{
                ...inputStyle,
                paddingLeft: 38,
                maxWidth: 360
              }} />
              {inventorySearch && <button onClick={() => setInventorySearch("")} style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                border: "none",
                background: "transparent",
                color: "#90A4AE",
                fontSize: 13,
                cursor: "pointer",
                padding: 4
              }} title="Clear search"><X className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>}
            </div>

            {flatRooms.length === 0 ? <EmptyBox text={`No rooms added yet — add rooms in the "Rooms & Washrooms" tab first.`} /> : inventoryRoomsFiltered.length === 0 ? <EmptyBox text={`No room matches "${inventorySearch}".`} /> : <div style={{
              background: "#FFFFFF",
              borderRadius: 16,
              border: `1px solid ${LIGHT}`,
              overflow: "auto",
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
            }}>
                <table style={{
                borderCollapse: "collapse",
                width: "100%",
                minWidth: 980
              }}>
                  <thead>
                    <tr style={{
                    background: PALE
                  }}>
                      <th style={{
                      ...thStyle,
                      position: "sticky",
                      left: 0,
                      background: PALE,
                      zIndex: 1,
                      minWidth: 190
                    }}>ROOM</th>
                      {INVENTORY_ITEMS.map(item => <th key={item.key} style={thStyle}>{item.icon} {item.label}</th>)}
                      <th style={thStyle}>STUDENTS</th>
                      <th style={thStyle}>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryRoomsFiltered.map((r, ri) => {
                    const isEditing = editingInventoryRoomId === r.id;
                    const saved = roomInventory[r.id] || blankInventory();
                    const inv = isEditing ? inventoryDraft || blankInventory() : saved;
                    const isSaving = inventorySavingRoomId === r.id;
                    return <tr key={r.id} style={{
                      borderTop: ri > 0 ? `1px solid ${PALE}` : "none",
                      background: isEditing ? "#FFFDE7" : "transparent"
                    }}>
                          <td style={{
                        ...tdStyle,
                        position: "sticky",
                        left: 0,
                        background: isEditing ? "#FFFDE7" : "#FFFFFF",
                        zIndex: 1
                      }}>
                            <div style={{
                          fontWeight: 800,
                          color: NAVY,
                          fontSize: 13
                        }}>Room {r.roomNo}</div>
                            <div style={{
                          fontSize: 11,
                          color: "#607D8B"
                        }}>{r.blockName} · Floor {r.floorNo} · Cap {r.capacity}</div>
                          </td>
                          {INVENTORY_ITEMS.map(item => <td key={item.key} style={tdStyle}>
                              {isEditing ? <input type="number" min="0" value={inv[item.key] ?? 0} onChange={e => updateInventoryDraft(item.key, e.target.value)} style={{
                          ...inputStyle,
                          width: 64,
                          textAlign: "center",
                          padding: "8px 6px"
                        }} /> : <div style={{
                          width: 64,
                          textAlign: "center",
                          fontSize: 13,
                          fontWeight: 700,
                          color: NAVY
                        }}>{saved[item.key] ?? 0}</div>}
                            </td>)}
                          <td style={{
                        ...tdStyle,
                        fontSize: 12,
                        color: "#607D8B",
                        fontWeight: 700
                      }}>{occupancyByRoom[r.id] || 0}/{r.capacity}</td>
                          <td style={{
                        ...tdStyle,
                        whiteSpace: "nowrap"
                      }}>
                            {isEditing ? <div style={{
                          display: "flex",
                          gap: 8
                        }}>
                                <button onClick={() => saveEditInventory(r.id)} disabled={isSaving} style={{
                            background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
                            color: "#FFFFFF",
                            border: "none",
                            padding: "7px 14px",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: isSaving ? "default" : "pointer",
                            opacity: isSaving ? 0.7 : 1
                          }}>{isSaving ? "Saving…" : "Save"}</button>
                                <button onClick={cancelEditInventory} disabled={isSaving} style={{
                            background: "#FFFFFF",
                            color: "#607D8B",
                            border: `1px solid ${LIGHT}`,
                            padding: "7px 14px",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer"
                          }}>Cancel</button>
                              </div> : <button onClick={() => startEditInventory(r.id)} style={{
                          background: PALE,
                          color: NAVY,
                          border: `1px solid ${LIGHT}`,
                          padding: "7px 14px",
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer"
                        }}><Pencil className="inline w-[1em] h-[1em] align-[-0.125em]" /> Edit</button>}
                          </td>
                        </tr>;
                  })}
                  </tbody>
                  <tfoot>
                    <tr style={{
                    borderTop: `2px solid ${LIGHT}`,
                    background: PALE
                  }}>
                      <td style={{
                      ...tdStyle,
                      position: "sticky",
                      left: 0,
                      background: PALE,
                      fontWeight: 800,
                      color: NAVY
                    }}>TOTAL</td>
                      {INVENTORY_ITEMS.map(item => <td key={item.key} style={{
                      ...tdStyle,
                      fontWeight: 800,
                      color: NAVY,
                      textAlign: "center"
                    }}>{inventoryTotals[item.key] || 0}</td>)}
                      <td style={tdStyle}></td>
                      <td style={tdStyle}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>}
          </div>

          {/* ── Sidebar: totals across all rooms ──────────────────────── */}
          <div style={{
            width: isMobile ? "100%" : 220,
            flexShrink: 0,
            position: isMobile ? "static" : "sticky",
            top: 20
          }}>
            <div style={{
              background: "#FFFFFF",
              borderRadius: 16,
              border: `1px solid ${LIGHT}`,
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              padding: 16
            }}>
              <div style={{
                fontSize: 12,
                fontWeight: 800,
                color: NAVY,
                letterSpacing: "0.4px",
                marginBottom: 12
              }}><BarChart3 className="inline w-[1em] h-[1em] align-[-0.125em]" /> TOTAL INVENTORY</div>
              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: 10
              }}>
                {INVENTORY_ITEMS.map(item => <StatCard key={item.key} label={item.label} value={inventoryTotals[item.key] || 0} color={NAVY} icon={item.icon} />)}
              </div>
            </div>
          </div>
        </div>}

      {/* ══════════════ MESSAGES TAB (Principal <-> Warden) ══════════════ */}
      {activeTab === "messages" && <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 300px",
          gap: 20,
          alignItems: "start"
        }}>
          <div style={{
            background: "#FFFFFF",
            borderRadius: 20,
            border: `1px solid ${LIGHT}`,
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            display: "flex",
            flexDirection: "column",
            height: 560
          }}>
            <div style={{
              padding: "14px 20px",
              borderBottom: `1px solid ${PALE}`
            }}>
              <div style={{
                fontWeight: 800,
                fontSize: 15,
                color: NAVY
              }}>Principal <ArrowLeftRight className="inline w-[1em] h-[1em] align-[-0.125em]" /> {WARDEN_LABEL}</div>
              <div style={{
                fontSize: 11.5,
                color: "#607D8B"
              }}>{HOSTEL_NAME} conversation</div>
            </div>

            <div style={{
              flex: 1,
              overflowY: "auto",
              padding: "18px 20px",
              display: "flex",
              flexDirection: "column",
              gap: 12
            }}>
              {messages.length === 0 ? <EmptyBox text="No messages yet. Start the conversation below." /> : messages.map(m => {
                const isPrincipal = m.from === "Principal";
                return <div key={m.id} style={{
                  display: "flex",
                  justifyContent: isPrincipal ? "flex-end" : "flex-start"
                }}>
                    <div style={{
                    maxWidth: "72%"
                  }}>
                      <div style={{
                      background: isPrincipal ? `linear-gradient(135deg, ${NAVY}, ${BLUE})` : PALE,
                      color: isPrincipal ? "#FFFFFF" : "#1A1A1A",
                      borderRadius: 16,
                      borderBottomRightRadius: isPrincipal ? 4 : 16,
                      borderBottomLeftRadius: isPrincipal ? 16 : 4,
                      padding: "10px 14px",
                      fontSize: 13,
                      lineHeight: 1.5
                    }}>
                        {m.priority === "Urgent" && <div style={{
                        fontSize: 10.5,
                        fontWeight: 800,
                        letterSpacing: "0.4px",
                        marginBottom: 4,
                        color: isPrincipal ? "#FFE0B2" : "#C62828"
                      }}><Circle className="inline w-[1em] h-[1em] align-[-0.125em]" /> URGENT</div>}
                        {m.body}
                      </div>
                      <div style={{
                      fontSize: 10.5,
                      color: "#90A4AE",
                      marginTop: 4,
                      textAlign: isPrincipal ? "right" : "left"
                    }}>
                        {isPrincipal ? "Principal" : `${WARDEN_LABEL}`} · {fmtDate(m.createdAt)}
                      </div>
                    </div>
                  </div>;
              })}
            </div>

            <div style={{
              padding: "14px 20px",
              borderTop: `1px solid ${PALE}`
            }}>
              {messageError && <div style={{
                background: "#FFEBEE",
                color: "#C62828",
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: 12.5,
                marginBottom: 10
              }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {messageError}</div>}
              <div style={{
                display: "flex",
                gap: 10
              }}>
                <textarea value={messageForm.body} onChange={e => setMessageForm(f => ({
                  ...f,
                  body: e.target.value
                }))} placeholder={`Message as ${WARDEN_LABEL}...`} rows={2} style={{
                  ...inputStyle,
                  resize: "vertical",
                  fontFamily: "inherit",
                  flex: 1
                }} onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }} />
                <button onClick={sendMessage} style={{
                  background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
                  color: "#FFFFFF",
                  border: "none",
                  padding: "0 22px",
                  borderRadius: 12,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer"
                }}>Send</button>
              </div>
            </div>
          </div>

          <div style={{
            background: "#FFFFFF",
            borderRadius: 16,
            border: `1px solid ${LIGHT}`,
            padding: 18,
            position: isMobile ? "static" : "sticky",
            top: 24
          }}>
            <div style={{
              fontWeight: 700,
              fontSize: 14,
              color: NAVY,
              marginBottom: 12
            }}><PenLine className="inline w-[1em] h-[1em] align-[-0.125em]" /> Sending As</div>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 14px",
              borderRadius: 12,
              border: `1px solid ${NAVY}`,
              background: NAVY,
              color: "#FFFFFF",
              fontSize: 12.5,
              fontWeight: 700,
              marginBottom: 18
            }}>
              <User className="inline w-[1em] h-[1em] align-[-0.125em]" /><School className="inline w-[1em] h-[1em] align-[-0.125em]" /> {WARDEN_LABEL}
            </div>

            <div style={{
              fontWeight: 700,
              fontSize: 14,
              color: NAVY,
              marginBottom: 10
            }}><Zap className="inline w-[1em] h-[1em] align-[-0.125em]" /> Priority</div>
            <div style={{
              display: "flex",
              gap: 8
            }}>
              {["Normal", "Urgent"].map(p => <button key={p} onClick={() => setMessageForm(f => ({
                ...f,
                priority: p
              }))} style={{
                flex: 1,
                padding: "8px 0",
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                border: `1px solid ${messageForm.priority === p ? p === "Urgent" ? "#C62828" : NAVY : LIGHT}`,
                background: messageForm.priority === p ? p === "Urgent" ? "#FFEBEE" : PALE : "#FFFFFF",
                color: messageForm.priority === p ? p === "Urgent" ? "#C62828" : NAVY : "#607D8B"
              }}>{p === "Urgent" ? "Urgent" : "Normal"}</button>)}
            </div>

            <div style={{
              fontSize: 11.5,
              color: "#90A4AE",
              marginTop: 16,
              lineHeight: 1.5
            }}>
              This page always sends as the {WARDEN_LABEL} — the Principal replies from their own dashboard's "Chat with {WARDEN_LABEL}" panel. Messages here are scoped to the {HOSTEL_NAME} only.
            </div>
          </div>
        </div>}
        </div>
      </div>
    </div>;
}
function StatCard({
  label,
  value,
  color,
  icon
}) {
  return <div style={{
    background: "#FFFFFF",
    borderRadius: 18,
    border: `1px solid ${LIGHT}`,
    padding: "16px 18px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
  }}>
      <div style={{
      width: 42,
      height: 42,
      borderRadius: 12,
      background: PALE,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 20,
      flexShrink: 0
    }}>{icon}</div>
      <div>
        <div style={{
        fontSize: 22,
        fontWeight: 800,
        color,
        lineHeight: 1
      }}>{value}</div>
        <div style={{
        fontSize: 11,
        color: "#607D8B",
        marginTop: 2,
        fontWeight: 600
      }}>{label}</div>
      </div>
    </div>;
}
function EmptyBox({
  text
}) {
  return <div style={{
    textAlign: "center",
    padding: 60,
    color: "#607D8B",
    background: "#FFFFFF",
    borderRadius: 20,
    border: `1px dashed ${LIGHT}`
  }}>
      {text}
    </div>;
}

// One Term cell in the Fee table — shows Paid / Pending for the Hostel Fee
// component of that term, read-only.
function FeeTermCell({
  t
}) {
  const sc = t && t.pending <= 0 && t.due > 0 ? FEE_STATUS_COLOR.Paid : FEE_STATUS_COLOR.Pending;
  return <div style={{
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2
  }}>
      <span style={{
      fontSize: 11.5,
      color: "#607D8B"
    }}>Paid ₹{(t?.paid || 0).toLocaleString()}</span>
      <span style={{
      background: sc.bg,
      color: sc.text,
      fontSize: 11,
      fontWeight: 700,
      padding: "2px 9px",
      borderRadius: 20
    }}>
        Pending ₹{(t?.pending || 0).toLocaleString()}
      </span>
    </div>;
}
const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: NAVY,
  letterSpacing: "0.5px",
  marginBottom: 6
};
const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  borderRadius: 10,
  border: `1px solid ${LIGHT}`,
  fontSize: 13,
  color: "#1A1A1A",
  outline: "none",
  boxSizing: "border-box"
};
const thStyle = {
  textAlign: "left",
  padding: "10px 14px",
  fontSize: 11,
  fontWeight: 700,
  color: NAVY,
  letterSpacing: "0.4px",
  whiteSpace: "nowrap"
};
const tdStyle = {
  padding: "10px 14px",
  verticalAlign: "middle"
};